import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Ban, CircleStop, Dices, Gift, History, Play, Radio, RefreshCw,
  Shield, Sparkles, Target, Unlock, Users, X, Zap,
} from "lucide-react";
import {
  LiveKitRoom, RoomAudioRenderer, VideoTrack, useConnectionState, useLocalParticipant, useTracks,
} from "@livekit/components-react";
import { ConnectionState, Track } from "livekit-client";
import "@livekit/components-styles";
import api, { formatApiError } from "@/lib/api";
import { useRole } from "@/context/RoleContext";
import { PageShell } from "@/pages/dashboard/RolePages";
import RewardsKD from "@/pages/dashboard/RewardsKD";
import TempleWheel, { TempleWheelEvent, TempleWheelSetup } from "@/components/TempleWheel";

const stateLabel = {
  live: "EN VIVO", available: "Disponible", busy: "Ocupado",
  offline: "Fuera de línea", blocked: "Bloqueado",
};

const offeringMotion = {
  spark: "kd9-reward-spark", ray: "kd9-reward-ray", flame: "kd9-reward-flame",
  portal: "kd9-reward-portal", seal: "kd9-reward-seal", crown: "kd9-reward-crown",
  eye: "kd9-reward-eye", star: "kd9-reward-star",
};

class TempleRuntimeBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("KD9 Templo Vivo runtime error", error, info);
  }

  recover = () => {
    this.setState({ error: null });
    this.props.onRecover?.();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black p-5">
        <div className="w-full max-w-sm rounded-3xl border border-cyan-300/35 bg-[#071018] p-6 text-center shadow-[0_0_70px_rgba(34,211,238,.18)]">
          <Radio className="mx-auto text-cyan-200" size={38} />
          <h2 className="mt-4 font-cinzel text-xl text-cyan-100">Templo Vivo necesita recuperarse</h2>
          <p className="mt-2 text-sm text-white/70">
            Se detuvo una capa de la experiencia antes de iniciar la transmisión. Tu sesión y tus capacidades siguen intactas.
          </p>
          <button type="button" className="kd9-btn-primary mt-5 min-h-12 w-full px-4" onClick={this.recover}>
            Volver al panel del Templo
          </button>
        </div>
      </div>
    );
  }
}

function OfferingBurst({ offering, goal, reducedMotion }) {
  if (!offering && !goal) return null;
  if (offering?.gift_code === "spark") {
    const combo = Math.max(1, Number(offering.combo) || 1);
    return (
      <div className={`pointer-events-none absolute inset-0 z-40 overflow-hidden kd9-spark-stage ${reducedMotion ? "kd9-spark-reduced" : ""}`}
        role="status" aria-live="polite" aria-label={`${offering.sender_name} encendió una Chispa KD. ${offering.energy_cost} KD de energía al templo.`}>
        <div className="kd9-spark-frame" aria-hidden="true" />
        <div className="kd9-spark-core" aria-hidden="true"><span>⚡</span></div>
        {!reducedMotion && Array.from({ length: 8 }, (_, index) => (
          <i key={index} className={`kd9-micro-spark kd9-micro-spark-${index + 1}`} aria-hidden="true" />
        ))}
        {!reducedMotion && <><i className="kd9-spark-edge kd9-spark-edge-a" aria-hidden="true" /><i className="kd9-spark-edge kd9-spark-edge-b" aria-hidden="true" /><i className="kd9-spark-edge kd9-spark-edge-c" aria-hidden="true" /></>}
        <div className="kd9-spark-radial" aria-hidden="true" />
        <div className="kd9-spark-copy">
          <strong>{offering.sender_name} encendió una chispa KD</strong>
          <span>+{offering.energy_cost} KD de energía al templo</span>
          {combo > 1 && <b>Chispa ×{combo}</b>}
        </div>
        <div className="kd9-spark-convergence" aria-hidden="true">⚡<small>KD9</small></div>
      </div>
    );
  }
  if (offering?.gift_code === "golden-ray") {
    return (
      <div className={`pointer-events-none absolute inset-0 z-40 overflow-hidden kd9-golden-ray-stage ${reducedMotion ? "kd9-golden-ray-reduced" : ""}`}
        role="status" aria-live="polite" aria-label={`${offering.sender_name} invocó un Rayo Dorado. ${offering.energy_cost} KD confirmados.`}>
        <div className="kd9-golden-ray-tension" aria-hidden="true" />
        {!reducedMotion && <div className="kd9-golden-ray-flash" aria-hidden="true" />}
        {!reducedMotion && (
          <svg className="kd9-golden-ray-bolt" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <path className="kd9-golden-ray-trunk" d="M56 -4 L49 18 L57 27 L43 43 L51 52 L38 71 L45 78 L37 103" />
            <path className="kd9-golden-ray-branch kd9-golden-ray-branch-a" d="M50 19 L31 29 L19 45" />
            <path className="kd9-golden-ray-branch kd9-golden-ray-branch-b" d="M45 42 L66 48 L79 63" />
            <path className="kd9-golden-ray-branch kd9-golden-ray-branch-c" d="M39 70 L22 77 L11 92" />
          </svg>
        )}
        <div className="kd9-golden-ray-frame" aria-hidden="true" />
        {!reducedMotion && <><i className="kd9-golden-border-arc kd9-golden-border-arc-a" aria-hidden="true" /><i className="kd9-golden-border-arc kd9-golden-border-arc-b" aria-hidden="true" /><i className="kd9-golden-border-arc kd9-golden-border-arc-c" aria-hidden="true" /><i className="kd9-golden-border-arc kd9-golden-border-arc-d" aria-hidden="true" /></>}
        <div className="kd9-golden-ray-copy">
          <strong>{offering.sender_name} invocó un Rayo Dorado</strong>
          <span>{offering.energy_cost} KD confirmados</span>
          {offering.queue_count > 0 && <b>{offering.queue_count} rayo{offering.queue_count > 1 ? "s" : ""} en cola</b>}
        </div>
        <div className="kd9-golden-ray-convergence" aria-hidden="true"><span>ϟ</span><small>KD9</small></div>
      </div>
    );
  }
  if (offering?.gift_code === "obsidian-flame") {
    const combo = Math.max(1, Number(offering.combo) || 1);
    return (
      <div className={`pointer-events-none absolute inset-0 z-40 overflow-hidden kd9-obsidian-stage ${reducedMotion ? "kd9-obsidian-reduced" : ""}`}
        role="status" aria-live="polite" aria-label={`${offering.sender_name} encendió la Llama Obsidiana. Transformación activada.`}>
        <div className="kd9-obsidian-veil" aria-hidden="true" />
        <div className="kd9-obsidian-ground-glow" aria-hidden="true" />
        <div className="kd9-obsidian-fissure" aria-hidden="true"><span /><span /><span /></div>
        {!reducedMotion && (
          <>
            <i className="kd9-obsidian-smoke kd9-obsidian-smoke-a" aria-hidden="true" />
            <i className="kd9-obsidian-smoke kd9-obsidian-smoke-b" aria-hidden="true" />
            <i className="kd9-obsidian-smoke kd9-obsidian-smoke-c" aria-hidden="true" />
          </>
        )}
        <div className="kd9-obsidian-flame-core" aria-hidden="true">
          <i className="kd9-obsidian-flame-tongue kd9-obsidian-flame-tongue-a" />
          <i className="kd9-obsidian-flame-tongue kd9-obsidian-flame-tongue-b" />
          <i className="kd9-obsidian-flame-tongue kd9-obsidian-flame-tongue-c" />
          <span>🔥</span>
        </div>
        {!reducedMotion && Array.from({ length: Math.min(18, Number(offering.effect?.ember_count) || 18) }, (_, index) => (
          <i key={index} className="kd9-obsidian-ember" aria-hidden="true"
            style={{
              left: `${10 + ((index * 23) % 80)}%`,
              bottom: `${6 + ((index * 13) % 24)}%`,
              animationDelay: `${1.8 + (index % 6) * .42}s`,
              animationDuration: `${4.2 + (index % 4) * .75}s`,
            }} />
        ))}
        <div className="kd9-obsidian-heat" aria-hidden="true" />
        <div className="kd9-obsidian-border" aria-hidden="true" />
        <div className="kd9-obsidian-copy">
          <strong>{offering.sender_name} encendió la Llama Obsidiana</strong>
          <span>Transformación activada</span>
          <small>+{offering.energy_cost} KD a la energía del Templo</small>
          {combo > 1 && <b>Llama ×{combo}</b>}
        </div>
        <div className="kd9-obsidian-afterglow" aria-hidden="true" />
      </div>
    );
  }
  if (offering?.gift_code === "portal-9") {
    const combo = Math.max(1, Number(offering.combo) || 1);
    const glyphs = ["9", "Δ", "𓂀", "◈", "ϟ", "☥", "◇", "✦", "9"];
    return (
      <div className={`pointer-events-none absolute inset-0 z-40 overflow-hidden kd9-portal9-stage ${reducedMotion ? "kd9-portal9-reduced" : ""}`}
        role="status" aria-live="polite" aria-label={`${offering.sender_name} abrió el Portal 9. Acceso ceremonial desbloqueado.`}>
        <div className="kd9-portal9-veil" aria-hidden="true" />
        <div className="kd9-portal9-space-fold" aria-hidden="true" />
        <div className="kd9-portal9-rift" aria-hidden="true" />
        <div className="kd9-portal9-vortex" aria-hidden="true">
          <i className="kd9-portal9-depth kd9-portal9-depth-a" />
          <i className="kd9-portal9-depth kd9-portal9-depth-b" />
          <i className="kd9-portal9-depth kd9-portal9-depth-c" />
          <span className="kd9-portal9-core">9</span>
        </div>
        <div className="kd9-portal9-ring kd9-portal9-ring-outer" aria-hidden="true" />
        <div className="kd9-portal9-ring kd9-portal9-ring-mid" aria-hidden="true" />
        <div className="kd9-portal9-ring kd9-portal9-ring-inner" aria-hidden="true" />
        {!reducedMotion && (
          <div className="kd9-portal9-glyph-orbit" aria-hidden="true">
            {glyphs.slice(0, Math.min(glyphs.length, Number(offering.effect?.glyph_count) || 9)).map((glyph, index) => (
              <i key={index} style={{ "--portal-glyph-angle": `${index * 40}deg` }}>{glyph}</i>
            ))}
          </div>
        )}
        {!reducedMotion && Array.from({ length: Math.min(24, Number(offering.effect?.particle_count) || 24) }, (_, index) => (
          <i key={index} className="kd9-portal9-particle" aria-hidden="true"
            style={{
              "--portal-particle-angle": `${(index * 137.5) % 360}deg`,
              "--portal-particle-radius": `${34 + (index % 6) * 8}vw`,
              animationDelay: `${2.4 + (index % 8) * .34}s`,
              animationDuration: `${4.8 + (index % 5) * .8}s`,
            }} />
        ))}
        <div className="kd9-portal9-grid" aria-hidden="true" />
        <div className="kd9-portal9-pulse" aria-hidden="true" />
        <div className="kd9-portal9-border" aria-hidden="true" />
        <div className="kd9-portal9-copy">
          <strong>{offering.sender_name} abrió el Portal 9</strong>
          <span>Acceso ceremonial desbloqueado</span>
          <small>+{offering.energy_cost} KD a la energía del Templo</small>
          {combo > 1 && <b>Portal ×{combo}</b>}
        </div>
        <div className="kd9-portal9-afterglow" aria-hidden="true" />
      </div>
    );
  }
  if (offering?.gift_code === "delta-seal") {
    const combo = Math.max(1, Number(offering.combo) || 1);
    const lineCount = Math.min(12, Number(offering.effect?.line_count) || 12);
    const particleCount = Math.min(30, Number(offering.effect?.particle_count) || 30);
    return (
      <div className={`pointer-events-none absolute inset-0 z-40 overflow-hidden kd9-delta-stage ${reducedMotion ? "kd9-delta-reduced" : ""}`}
        role="status" aria-live="polite" aria-label={`${offering.sender_name} activó el Sello Delta. Integración y propósito.`}>
        <div className="kd9-delta-veil" aria-hidden="true" />
        <div className="kd9-delta-sacred-grid" aria-hidden="true" />
        {!reducedMotion && Array.from({ length: lineCount }, (_, index) => (
          <i key={index} className="kd9-delta-convergence-line" aria-hidden="true"
            style={{ "--delta-line-angle": `${index * (360 / lineCount)}deg`, animationDelay: `${.9 + index * .11}s` }} />
        ))}
        <div className="kd9-delta-triangle-wrap" aria-hidden="true">
          <svg className="kd9-delta-svg" viewBox="0 0 320 300">
            <defs>
              <linearGradient id="deltaSealGradient" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#67e8f9" />
                <stop offset="48%" stopColor="#ffffff" />
                <stop offset="100%" stopColor="#f7cf4b" />
              </linearGradient>
            </defs>
            <path className="kd9-delta-stroke kd9-delta-stroke-a" d="M160 24 L286 260" />
            <path className="kd9-delta-stroke kd9-delta-stroke-b" d="M286 260 L34 260" />
            <path className="kd9-delta-stroke kd9-delta-stroke-c" d="M34 260 L160 24" />
            <path className="kd9-delta-inner" d="M160 88 L225 224 L95 224 Z" />
          </svg>
          <span className="kd9-delta-core-mark">Δ</span>
        </div>
        <div className="kd9-delta-orbit kd9-delta-orbit-a" aria-hidden="true" />
        <div className="kd9-delta-orbit kd9-delta-orbit-b" aria-hidden="true" />
        <div className="kd9-delta-orbit kd9-delta-orbit-c" aria-hidden="true" />
        <div className="kd9-delta-orbit kd9-delta-orbit-d" aria-hidden="true" />
        {!reducedMotion && Array.from({ length: particleCount }, (_, index) => (
          <i key={index} className="kd9-delta-fragment" aria-hidden="true"
            style={{
              "--delta-fragment-angle": `${(index * 127.4) % 360}deg`,
              "--delta-fragment-distance": `${18 + (index % 7) * 7}vmin`,
              animationDelay: `${4.4 + (index % 9) * .19}s`,
            }} />
        ))}
        <div className="kd9-delta-impact" aria-hidden="true" />
        <div className="kd9-delta-shockwave kd9-delta-shockwave-a" aria-hidden="true" />
        <div className="kd9-delta-shockwave kd9-delta-shockwave-b" aria-hidden="true" />
        <div className="kd9-delta-shockwave kd9-delta-shockwave-c" aria-hidden="true" />
        <div className="kd9-delta-corners" aria-hidden="true"><i/><i/><i/><i/></div>
        <div className="kd9-delta-copy">
          <strong>{offering.sender_name} activó el Sello Delta</strong>
          <span>Integración y propósito</span>
          <small>+{offering.energy_cost} KD a la energía del Templo</small>
          {combo > 1 && <b>Sello ×{combo}</b>}
        </div>
        <div className="kd9-delta-afterglow" aria-hidden="true" />
      </div>
    );
  }
  if (offering?.gift_code === "solar-crown") {
    const combo = Math.max(1, Number(offering.combo) || 1);
    const rayCount = Math.min(16, Number(offering.effect?.ray_count) || 16);
    const particleCount = Math.min(36, Number(offering.effect?.particle_count) || 36);
    return (
      <div className={`pointer-events-none absolute inset-0 z-40 overflow-hidden kd9-solar-stage ${reducedMotion ? "kd9-solar-reduced" : ""}`}
        role="status" aria-live="polite" aria-label={`${offering.sender_name} recibió la Corona Solar. Presencia, elevación y soberanía.`}>
        <div className="kd9-solar-veil" aria-hidden="true" />
        <div className="kd9-solar-horizon" aria-hidden="true" />
        <div className="kd9-solar-light-pillar" aria-hidden="true" />
        {!reducedMotion && Array.from({ length: rayCount }, (_, index) => (
          <i key={index} className="kd9-solar-ray" aria-hidden="true"
            style={{ "--solar-ray-angle": `${index * (360 / rayCount)}deg`, animationDelay: `${1.1 + (index % 8) * .12}s` }} />
        ))}
        <div className="kd9-solar-core" aria-hidden="true">
          <span className="kd9-solar-disc" />
          <span className="kd9-solar-core-glow" />
        </div>
        <div className="kd9-solar-halo kd9-solar-halo-a" aria-hidden="true" />
        <div className="kd9-solar-halo kd9-solar-halo-b" aria-hidden="true" />
        <div className="kd9-solar-halo kd9-solar-halo-c" aria-hidden="true" />
        <div className="kd9-solar-halo kd9-solar-halo-d" aria-hidden="true" />
        <div className="kd9-solar-halo kd9-solar-halo-e" aria-hidden="true" />
        <div className="kd9-solar-crown-wrap" aria-hidden="true">
          <svg className="kd9-solar-crown-svg" viewBox="0 0 360 220">
            <defs>
              <linearGradient id="solarCrownGradient" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#fff9d9" />
                <stop offset="38%" stopColor="#ffd84d" />
                <stop offset="72%" stopColor="#f59e0b" />
                <stop offset="100%" stopColor="#fff3b0" />
              </linearGradient>
            </defs>
            <path className="kd9-solar-crown-path" d="M38 172 L58 66 L123 126 L181 34 L238 126 L303 66 L323 172 Z" />
            <path className="kd9-solar-crown-base" d="M52 174 Q181 199 309 174 L300 204 Q181 218 60 204 Z" />
            <circle className="kd9-solar-jewel" cx="181" cy="166" r="13" />
          </svg>
        </div>
        {!reducedMotion && Array.from({ length: particleCount }, (_, index) => (
          <i key={index} className="kd9-solar-dust" aria-hidden="true"
            style={{
              left: `${5 + ((index * 29) % 90)}%`,
              top: `${8 + ((index * 41) % 78)}%`,
              animationDelay: `${2.1 + (index % 10) * .21}s`,
              animationDuration: `${4.4 + (index % 5) * .7}s`,
            }} />
        ))}
        {!reducedMotion && <>
          <div className="kd9-solar-flare kd9-solar-flare-a" aria-hidden="true" />
          <div className="kd9-solar-flare kd9-solar-flare-b" aria-hidden="true" />
          <div className="kd9-solar-flare kd9-solar-flare-c" aria-hidden="true" />
        </>}
        <div className="kd9-solar-coronation-pulse" aria-hidden="true" />
        <div className="kd9-solar-border" aria-hidden="true" />
        <div className="kd9-solar-copy">
          <strong>{offering.sender_name} recibió la Corona Solar</strong>
          <span>Presencia · Elevación · Soberanía</span>
          <small>+{offering.energy_cost} KD a la energía del Templo</small>
          {combo > 1 && <b>Corona ×{combo}</b>}
        </div>
        <div className="kd9-solar-afterglow" aria-hidden="true" />
      </div>
    );
  }
  if (offering?.gift_code === "khaos-eye") {
    const combo = Math.max(1, Number(offering.combo) || 1);
    const particleCount = Math.min(42, Number(offering.effect?.particle_count) || 42);
    const glyphs = ["𓂀", "Δ", "9", "◈", "ϟ", "☥", "◇", "✦", "𓂀"];
    return (
      <div className={`pointer-events-none absolute inset-0 z-40 overflow-hidden kd9-khaos-stage ${reducedMotion ? "kd9-khaos-reduced" : ""}`}
        role="status" aria-live="polite" aria-label={`${offering.sender_name} abrió el Ojo de Khaos. Visión profunda activada.`}>
        <div className="kd9-khaos-veil" aria-hidden="true" />
        <div className="kd9-khaos-distortion" aria-hidden="true" />
        <div className="kd9-khaos-eye-shell" aria-hidden="true">
          <div className="kd9-khaos-lid kd9-khaos-lid-top" />
          <div className="kd9-khaos-lid kd9-khaos-lid-bottom" />
          <div className="kd9-khaos-iris">
            <i className="kd9-khaos-iris-ring kd9-khaos-iris-a" />
            <i className="kd9-khaos-iris-ring kd9-khaos-iris-b" />
            <i className="kd9-khaos-iris-ring kd9-khaos-iris-c" />
            <i className="kd9-khaos-iris-ring kd9-khaos-iris-d" />
            <i className="kd9-khaos-iris-ring kd9-khaos-iris-e" />
            <i className="kd9-khaos-iris-ring kd9-khaos-iris-f" />
            <span className="kd9-khaos-pupil">𓂀</span>
            <span className="kd9-khaos-pupil-core" />
          </div>
        </div>
        {!reducedMotion && (
          <div className="kd9-khaos-glyph-orbit" aria-hidden="true">
            {glyphs.slice(0, Math.min(9, Number(offering.effect?.glyph_count) || 9)).map((glyph, index) => (
              <i key={index} style={{ "--khaos-glyph-angle": `${index * 40}deg` }}>{glyph}</i>
            ))}
          </div>
        )}
        {!reducedMotion && Array.from({ length: particleCount }, (_, index) => (
          <i key={index} className="kd9-khaos-particle" aria-hidden="true"
            style={{
              "--khaos-x": `${6 + ((index * 23) % 88)}%`,
              "--khaos-y": `${8 + ((index * 37) % 80)}%`,
              animationDelay: `${2.2 + (index % 11) * .18}s`,
              animationDuration: `${4.8 + (index % 6) * .65}s`,
            }} />
        ))}
        <div className="kd9-khaos-scan kd9-khaos-scan-a" aria-hidden="true" />
        <div className="kd9-khaos-scan kd9-khaos-scan-b" aria-hidden="true" />
        <div className="kd9-khaos-pulse kd9-khaos-pulse-a" aria-hidden="true" />
        <div className="kd9-khaos-pulse kd9-khaos-pulse-b" aria-hidden="true" />
        <div className="kd9-khaos-pulse kd9-khaos-pulse-c" aria-hidden="true" />
        <div className="kd9-khaos-border" aria-hidden="true" />
        <div className="kd9-khaos-copy">
          <strong>{offering.sender_name} abrió el Ojo de Khaos</strong>
          <span>Visión · Profundidad · Revelación</span>
          <small>+{offering.energy_cost} KD a la energía del Templo</small>
          {combo > 1 && <b>Ojo ×{combo}</b>}
        </div>
        <div className="kd9-khaos-afterglow" aria-hidden="true" />
      </div>
    );
  }
  if (offering?.gift_code === "ancestral-star") {
    const combo = Math.max(1, Number(offering.combo) || 1);
    const particleCount = Math.min(54, Number(offering.effect?.particle_count) || 54);
    const constellationNodes = Math.min(18, Number(offering.effect?.constellation_nodes) || 18);
    const rayCount = Math.min(18, Number(offering.effect?.ray_count) || 18);
    return (
      <div className={`pointer-events-none absolute inset-0 z-40 overflow-hidden kd9-ancestral-stage ${reducedMotion ? "kd9-ancestral-reduced" : ""}`}
        role="status" aria-live="polite" aria-label={`${offering.sender_name} encendió la Estrella Ancestral. Su energía deja legado en el Templo.`}>
        <div className="kd9-ancestral-veil" aria-hidden="true" />
        <div className="kd9-ancestral-cosmos" aria-hidden="true" />
        {!reducedMotion && Array.from({ length: constellationNodes }, (_, index) => (
          <i key={index} className="kd9-ancestral-node" aria-hidden="true"
            style={{
              "--ancestor-x": `${8 + ((index * 31) % 84)}%`,
              "--ancestor-y": `${10 + ((index * 43) % 72)}%`,
              animationDelay: `${1.2 + (index % 9) * .16}s`,
            }} />
        ))}
        <div className="kd9-ancestral-constellation-lines" aria-hidden="true">
          <i/><i/><i/><i/><i/><i/><i/><i/><i/>
        </div>
        {!reducedMotion && Array.from({ length: rayCount }, (_, index) => (
          <i key={index} className="kd9-ancestral-ray" aria-hidden="true"
            style={{ "--ancestor-ray-angle": `${index * (360 / rayCount)}deg`, animationDelay: `${3 + (index % 9) * .1}s` }} />
        ))}
        <div className="kd9-ancestral-halo kd9-ancestral-halo-a" aria-hidden="true" />
        <div className="kd9-ancestral-halo kd9-ancestral-halo-b" aria-hidden="true" />
        <div className="kd9-ancestral-halo kd9-ancestral-halo-c" aria-hidden="true" />
        <div className="kd9-ancestral-halo kd9-ancestral-halo-d" aria-hidden="true" />
        <div className="kd9-ancestral-halo kd9-ancestral-halo-e" aria-hidden="true" />
        <div className="kd9-ancestral-halo kd9-ancestral-halo-f" aria-hidden="true" />
        <div className="kd9-ancestral-star-core" aria-hidden="true">
          <span className="kd9-ancestral-star-shape">✦</span>
          <span className="kd9-ancestral-star-heart">✦</span>
        </div>
        {!reducedMotion && Array.from({ length: particleCount }, (_, index) => (
          <i key={index} className="kd9-ancestral-particle" aria-hidden="true"
            style={{
              left: `${4 + ((index * 19) % 92)}%`,
              top: `${6 + ((index * 47) % 84)}%`,
              animationDelay: `${2.2 + (index % 12) * .17}s`,
              animationDuration: `${4.6 + (index % 7) * .55}s`,
            }} />
        ))}
        <div className="kd9-ancestral-legacy-wave kd9-ancestral-legacy-wave-a" aria-hidden="true" />
        <div className="kd9-ancestral-legacy-wave kd9-ancestral-legacy-wave-b" aria-hidden="true" />
        <div className="kd9-ancestral-legacy-wave kd9-ancestral-legacy-wave-c" aria-hidden="true" />
        <div className="kd9-ancestral-border" aria-hidden="true" />
        <div className="kd9-ancestral-copy">
          <strong>{offering.sender_name} encendió la Estrella Ancestral</strong>
          <span>Memoria · Legado · Trascendencia</span>
          <small>+{offering.energy_cost} KD a la energía del Templo</small>
          <em>Tu energía deja legado en el Templo.</em>
          {combo > 1 && <b>Estrella ×{combo}</b>}
        </div>
        <div className="kd9-ancestral-afterglow" aria-hidden="true" />
      </div>
    );
  }
  return (
    <div className={`pointer-events-none absolute inset-0 z-40 flex items-center justify-center kd9-reward-stage kd9-reward-stage-${offering?.animation || "goal"}`}>
      {offering?.animation === "ray" && <span className="kd9-ray-streak" />}
      {offering?.animation === "portal" && <span className="kd9-portal-ring" />}
      {offering?.animation === "seal" && <span className="kd9-delta-trace">Δ</span>}
      {offering?.animation === "crown" && <span className="kd9-solar-halo" />}
      {offering?.animation === "eye" && <span className="kd9-khaos-eye">𓂀</span>}
      {offering?.animation === "star" && <span className="kd9-ancestral-sky" />}
      {!reducedMotion && offering && Array.from({ length: Math.min(18, 6 + (offering.priority || 1) * 3) }, (_, index) => (
        <span key={index} className="absolute animate-ping text-xl text-[#f4d47c]" style={{ left: `${8 + ((index * 17) % 84)}%`, top: `${12 + ((index * 29) % 72)}%`, animationDelay: `${(index % 5) * 120}ms` }}>✦</span>
      ))}
      <div className={`max-w-[86vw] rounded-full border px-10 py-8 text-center ${offering?.tier === "legendary" ? "border-violet-300 bg-violet-950/90 shadow-[0_0_120px_rgba(139,92,246,.95)]" : offering?.tier === "epic" ? "border-cyan-300 bg-slate-950/90 shadow-[0_0_100px_rgba(34,211,238,.8)]" : "border-[#c69a3a]/70 bg-black/90 shadow-[0_0_90px_rgba(198,154,58,.8)]"}`}>
        <div className={`text-7xl ${reducedMotion ? "" : (goal ? "animate-pulse" : offeringMotion[offering?.animation] || "animate-pulse")}`}>
          {goal ? "⚡" : offering?.symbol}
        </div>
        <p className="mt-3 font-cinzel text-2xl kd9-gold-text">
          {goal ? "¡Meta del Templo alcanzada!" : `${offering?.gift_name}${offering?.combo > 1 ? ` · COMBO x${offering.combo}` : ""}`}
        </p>
        {goal
          ? <p className="mt-1 text-sm text-white/85">Recompensa auditada: {goal.reward_energy} KD</p>
          : <>
              <p className="mt-1 text-sm text-white/80">Ofrenda de {offering?.sender_name}</p>
              <p className="mt-1 font-cinzel text-lg text-cyan-100">+{offering?.energy_cost || 0} a la Meta</p>
              {offering?.message && <p className="mt-2 font-cormorant text-xl text-white">“{offering.message}”</p>}
              {offering?.effect?.legacy && <p className="mt-2 font-cormorant text-xl text-violet-100">{offering.effect.legacy}</p>}
            </>}
      </div>
    </div>
  );
}

function WheelExperienceOverlay({ experience, wheel, onSpinComplete }) {
  if (!experience) return null;
  const event = experience.event;
  const playerName = event.player_name || "Un miembro KD9";
  return (
    <section className="kd9-global-wheel-event pointer-events-none absolute inset-0 z-[80] grid place-items-center overflow-hidden bg-black/55 p-4 backdrop-blur-[2px]"
      aria-live="assertive" aria-label="Acontecimiento global de la Ruleta del Templo" data-testid="global-wheel-event">
      {experience.phase === "notice" && (
        <div className="kd9-wheel-announcement rounded-2xl border border-violet-300/55 bg-[#090613]/95 px-6 py-4 text-center shadow-[0_0_55px_rgba(139,92,246,.42)]">
          <Dices className="mx-auto text-violet-200" size={30} aria-hidden="true" />
          <p className="mt-2 font-cinzel text-lg text-white">{playerName} activó la ruleta del templo</p>
        </div>
      )}
      {experience.phase === "spin" && (
        <div className="kd9-wheel-global-stage w-full text-center">
          <p className="mb-5 font-cinzel text-sm uppercase tracking-[.18em] text-violet-100">{playerName} activó la ruleta del templo</p>
          <TempleWheelEvent wheel={wheel} event={event} onComplete={onSpinComplete} />
        </div>
      )}
      {experience.phase === "result" && (
        <div className="kd9-wheel-global-result w-full max-w-sm rounded-3xl border border-cyan-200/60 bg-[#050816]/96 p-6 text-center shadow-[0_0_80px_rgba(34,211,238,.38)]">
          <span className="text-4xl" aria-hidden="true">⚡</span>
          <p className="mt-3 text-xs uppercase tracking-[.2em] text-cyan-200">Resultado de {playerName}</p>
          <strong className="mt-2 block font-cinzel text-2xl text-white">{event.label}</strong>
          {event.payload && <p className="mt-2 text-sm text-white/75">{event.payload}</p>}
        </div>
      )}
    </section>
  );
}

function HostVideo({ isHost }) {
  const tracks = useTracks([Track.Source.Camera], { onlySubscribed: false });
  const cameraTracks = tracks.filter((item) => item.publication || item.participant?.isLocal);
  const hostTrack = isHost
    ? (cameraTracks.find((item) => item.participant?.isLocal) || cameraTracks[0])
    : (cameraTracks.find((item) => !item.participant?.isLocal) || cameraTracks[0]);
  if (!hostTrack?.publication) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-black">
        <div className="px-6 text-center">
          <Radio className="mx-auto kd9-gold-text animate-pulse" size={54} />
          <p className="mt-3 text-sm text-white/70">{isHost ? "Activando tu cámara…" : "Esperando la cámara del anfitrión…"}</p>
          <p className="mt-1 text-xs text-white/45">La sesión permanece conectada mientras aparece el video.</p>
        </div>
      </div>
    );
  }
  return <VideoTrack trackRef={hostTrack} className="absolute inset-0 h-full w-full bg-black object-cover" />;
}

function liveConnectionErrorMessage(error) {
  const name = String(error?.name || "");
  const message = String(error?.message || "");
  if (["NotAllowedError", "PermissionDeniedError"].includes(name) || /permission|denied|not allowed/i.test(message)) {
    return "KD9 no tiene permiso para usar la cámara o el micrófono. Habilítalos para este sitio y pulsa Reintentar cámara.";
  }
  if (name === "NotFoundError" || /device.*not found|requested device not found/i.test(message)) {
    return "No encontramos una cámara o micrófono disponible en este dispositivo.";
  }
  if (name === "NotReadableError" || /could not start video source|device.*busy/i.test(message)) {
    return "La cámara o el micrófono están siendo usados por otra aplicación. Ciérrala y pulsa Reintentar cámara.";
  }
  return message ? `No pudimos abrir el video del Templo Vivo: ${message}` : "No pudimos abrir el video del Templo Vivo. Intenta nuevamente.";
}

function HostMediaPublisher({ isHost, retryKey, onState }) {
  const { localParticipant } = useLocalParticipant();
  const connectionState = useConnectionState();

  useEffect(() => {
    if (!isHost || !localParticipant || connectionState !== ConnectionState.Connected) return undefined;
    let active = true;

    const publish = async () => {
      onState({ status: "starting", message: "Activando cámara y micrófono…" });
      const [camera, microphone] = await Promise.allSettled([
        localParticipant.setCameraEnabled(true),
        localParticipant.setMicrophoneEnabled(true),
      ]);
      if (!active) return;

      if (camera.status === "rejected") {
        onState({ status: "error", message: liveConnectionErrorMessage(camera.reason) });
        return;
      }
      if (microphone.status === "rejected") {
        onState({
          status: "warning",
          message: "La cámara está activa, pero el micrófono no pudo habilitarse. Revisa el permiso de micrófono.",
        });
        return;
      }
      onState({ status: "ready", message: "" });
    };

    publish();
    return () => { active = false; };
  }, [connectionState, isHost, localParticipant, onState, retryKey]);

  return null;
}

function LiveExperience({ connection, selected, catalog, wallet, onWallet, onClose, onReconnect }) {
  const [activePanel, setActivePanel] = useState(null);
  const [presence, setPresence] = useState({ count: 0, peak: 0, viewers: [], system_events: [] });
  const [pulse, setPulse] = useState(null);
  const [wheel, setWheel] = useState({ enabled: false, status: "draft", title: "Ruleta del Templo", options: [], spin_cost_energy: 0 });
  const [wheelExperience, setWheelExperience] = useState(null);
  const [wheelResult, setWheelResult] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(() => globalThis.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches || false);
  const [chat, setChat] = useState([]);
  const [joinEvents, setJoinEvents] = useState([]);
  const [message, setMessage] = useState("");
  const [sendingComment, setSendingComment] = useState(false);
  const [giftMessage, setGiftMessage] = useState("");
  const [sendingGiftId, setSendingGiftId] = useState(null);
  const [needsRecharge, setNeedsRecharge] = useState(false);
  const [goal, setGoal] = useState({ target_energy: 3000, progress_energy: 0, percent: 0, reached: false });
  const [burst, setBurst] = useState(null);
  const [offeringQueueCount, setOfferingQueueCount] = useState(0);
  const [goalBurst, setGoalBurst] = useState(null);
  const [rays, setRays] = useState([]);
  const [error, setError] = useState("");
  const [exitPromptOpen, setExitPromptOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [roomAttempt, setRoomAttempt] = useState(0);
  const [roomStatus, setRoomStatus] = useState("connecting");
  const [mediaRetry, setMediaRetry] = useState(0);
  const [mediaState, setMediaState] = useState({ status: connection.is_host ? "idle" : "ready", message: "" });
  const lastOffering = useRef(null);
  const lastGoalEvent = useRef(null);
  const lastTapEvent = useRef(null);
  const tapQueue = useRef(0);
  const offeringQueue = useRef([]);
  const showingOffering = useRef(false);
  const seenOfferingIds = useRef(new Set());
  const seenPresenceEventIdsRef = useRef(new Set());
  const presenceEventsInitializedRef = useRef(false);
  const liveViewRef = useRef({ activePanel: null });
  const exitConfirmedRef = useRef(false);
  const reconnectTimerRef = useRef(null);
  const reconnectingRef = useRef(false);
  const effectTimersRef = useRef(new Set());
  const audioContextsRef = useRef(new Set());
  const localOfferingIdsRef = useRef(new Set());
  const chatInputRef = useRef(null);
  const sendingCommentRef = useRef(false);
  const sendingGiftRef = useRef(false);
  const offeringKeysRef = useRef(new Map());
  const spinningRef = useRef(false);
  const wheelSpinKeyRef = useRef("");
  const chatHistoryRef = useRef(null);
  const seenChatIdsRef = useRef(new Set());
  const wheelEventQueueRef = useRef([]);
  const showingWheelEventRef = useRef(false);
  const seenWheelEventIdsRef = useRef(new Set());
  const advanceWheelEventRef = useRef(null);
  const sessionId = selected.session_id;
  const giftOpen = activePanel === "offerings";
  const gamesOpen = activePanel === "games";
  const hostPanelOpen = activePanel === "host";

  liveViewRef.current = { activePanel };

  const togglePanel = useCallback((panel) => {
    setActivePanel((current) => current === panel ? null : panel);
  }, []);

  const appendChat = useCallback((incoming) => {
    const rows = Array.isArray(incoming) ? incoming : [incoming];
    setChat((current) => {
      const next = [...current];
      rows.forEach((item) => {
        if (!item?.id || seenChatIdsRef.current.has(item.id)) return;
        seenChatIdsRef.current.add(item.id);
        next.push(item);
      });
      return next.slice(-200);
    });
  }, []);

  const processPresenceEvents = useCallback((events) => {
    const rows = Array.isArray(events) ? [...events].reverse() : [];
    if (!presenceEventsInitializedRef.current) {
      rows.forEach((item) => item?.id && seenPresenceEventIdsRef.current.add(item.id));
      presenceEventsInitializedRef.current = true;
      return;
    }
    const fresh = rows.filter((item) => item?.id && !seenPresenceEventIdsRef.current.has(item.id));
    if (!fresh.length) return;
    fresh.forEach((item) => seenPresenceEventIdsRef.current.add(item.id));
    setJoinEvents((current) => [...current, ...fresh].slice(-16));
  }, []);

  const requestExit = useCallback(() => {
    if (!exitConfirmedRef.current) setExitPromptOpen(true);
  }, []);

  const confirmExit = useCallback(() => {
    if (exitConfirmedRef.current) return;
    exitConfirmedRef.current = true;
    setLeaving(true);
    window.clearTimeout(reconnectTimerRef.current);
    onClose();
    if (window.history.state?.kd9TempleGuard === sessionId) window.history.back();
  }, [onClose, sessionId]);

  useEffect(() => {
    const guardState = { ...(window.history.state || {}), kd9TempleGuard: sessionId };
    window.history.pushState(guardState, "", window.location.href);

    const onTempleBack = () => {
      if (exitConfirmedRef.current) return;
      const currentView = liveViewRef.current;
      if (currentView.activePanel) setActivePanel(null);
      else setExitPromptOpen(true);

      // Restore the per-session guard consumed by Android's button or back gesture.
      window.history.pushState(guardState, "", window.location.href);
    };

    window.addEventListener("popstate", onTempleBack);
    return () => {
      window.removeEventListener("popstate", onTempleBack);
      window.clearTimeout(reconnectTimerRef.current);
    };
  }, [sessionId]);

  const handleDisconnected = useCallback(() => {
    if (exitConfirmedRef.current || reconnectingRef.current) return;
    reconnectingRef.current = true;
    setRoomStatus("reconnecting");
    setError("Conexión interrumpida. Renovando acceso y reconectando con este Templo Vivo…");
    window.clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = window.setTimeout(async () => {
      try {
        await onReconnect(sessionId);
        setRoomAttempt((attempt) => attempt + 1);
      } catch (_) {
        reconnectingRef.current = false;
        setError("No pudimos recuperar el video. Pulsa Reanudar transmisión o vuelve a entrar al live.");
      }
    }, 900);
  }, [onReconnect, sessionId]);

  const scheduleEffect = useCallback((callback, delay) => {
    const timer = window.setTimeout(() => {
      effectTimersRef.current.delete(timer);
      callback();
    }, delay);
    effectTimersRef.current.add(timer);
    return timer;
  }, []);

  advanceWheelEventRef.current = () => {
    const next = wheelEventQueueRef.current.shift();
    if (!next) {
      showingWheelEventRef.current = false;
      setWheelExperience(null);
      return;
    }
    showingWheelEventRef.current = true;
    setActivePanel(null);
    setWheelExperience({ event: next, phase: "notice" });
    scheduleEffect(() => {
      setWheelExperience((current) => current?.event?.id === next.id ? { event: next, phase: "spin" } : current);
    }, 1100);
  };

  const enqueueWheelEvent = useCallback((event) => {
    if (!event?.id || seenWheelEventIdsRef.current.has(event.id)) return;
    seenWheelEventIdsRef.current.add(event.id);
    wheelEventQueueRef.current.push(event);
    setActivePanel(null);
    if (!showingWheelEventRef.current) advanceWheelEventRef.current?.();
  }, []);

  const finishWheelExperience = useCallback((event) => {
    setWheelExperience((current) => current?.event?.id === event.id ? { event, phase: "result" } : current);
    scheduleEffect(() => {
      setWheelExperience(null);
      scheduleEffect(() => {
        showingWheelEventRef.current = false;
        advanceWheelEventRef.current?.();
      }, 180);
    }, 3000);
  }, [scheduleEffect]);

  const playOfferingSound = useCallback((item) => {
    const effect = item?.effect || {};
    const isSpark = item?.gift_code === "spark";
    const isGoldenRay = item?.gift_code === "golden-ray";
    const isObsidianFlame = item?.gift_code === "obsidian-flame";
    const isPortal9 = item?.gift_code === "portal-9";
    const isDeltaSeal = item?.gift_code === "delta-seal";
    const isSolarCrown = item?.gift_code === "solar-crown";
    const isKhaosEye = item?.gift_code === "khaos-eye";
    const isAncestralStar = item?.gift_code === "ancestral-star";
    const localSender = localOfferingIdsRef.current.has(item?.id);
    if (navigator.vibrate && effect.haptic?.length) navigator.vibrate(effect.haptic);
    if (isSpark && localSender && navigator.vibrate) {
      scheduleEffect(() => navigator.vibrate([45, 65, 45]), reducedMotion ? 700 : 5200);
    }
    if (isGoldenRay && navigator.vibrate) {
      navigator.vibrate(reducedMotion ? [80] : [90]);
      if (!reducedMotion) scheduleEffect(() => navigator.vibrate([160, 180, 70, 90, 70]), 4050);
    }
    if (isObsidianFlame && navigator.vibrate && !reducedMotion) {
      scheduleEffect(() => navigator.vibrate(effect.ignition_haptic_ms || [75, 55, 95]), 2550);
      scheduleEffect(() => navigator.vibrate(effect.peak_haptic_ms || [120, 80, 140]), 5650);
    }
    if (isPortal9 && navigator.vibrate && !reducedMotion) {
      scheduleEffect(() => navigator.vibrate(effect.opening_haptic_ms || [80, 55, 110]), 5050);
      scheduleEffect(() => navigator.vibrate(effect.peak_haptic_ms || [150, 90, 180]), 13050);
      scheduleEffect(() => navigator.vibrate(effect.closing_haptic_ms || [45, 35, 60]), 16050);
    }
    if (isDeltaSeal && navigator.vibrate && !reducedMotion) {
      scheduleEffect(() => navigator.vibrate(effect.trace_haptic_ms || [70, 45, 90]), 2250);
      scheduleEffect(() => navigator.vibrate(effect.impact_haptic_ms || [180, 90, 220]), 9650);
      scheduleEffect(() => navigator.vibrate(effect.resonance_haptic_ms || [55, 40, 80]), 14050);
    }
    if (isSolarCrown && navigator.vibrate && !reducedMotion) {
      scheduleEffect(() => navigator.vibrate(effect.sunrise_haptic_ms || [55, 40, 75]), 2550);
      scheduleEffect(() => navigator.vibrate(effect.coronation_haptic_ms || [120, 70, 150]), 6050);
      scheduleEffect(() => navigator.vibrate(effect.peak_haptic_ms || [180, 90, 220]), 14050);
      scheduleEffect(() => navigator.vibrate(effect.closing_haptic_ms || [50, 35, 70]), 17050);
    }
    if (isKhaosEye && navigator.vibrate && !reducedMotion) {
      scheduleEffect(() => navigator.vibrate(effect.awakening_haptic_ms || [65, 45, 95]), 2450);
      scheduleEffect(() => navigator.vibrate(effect.focus_haptic_ms || [115, 70, 145]), 6050);
      scheduleEffect(() => navigator.vibrate(effect.vision_haptic_ms || [175, 95, 230]), 14050);
      scheduleEffect(() => navigator.vibrate(effect.closing_haptic_ms || [55, 40, 80]), 17050);
    }
    if (isAncestralStar && navigator.vibrate && !reducedMotion) {
      scheduleEffect(() => navigator.vibrate(effect.awakening_haptic_ms || [60, 40, 85]), 2450);
      scheduleEffect(() => navigator.vibrate(effect.constellation_haptic_ms || [95, 65, 125]), 6050);
      scheduleEffect(() => navigator.vibrate(effect.legacy_haptic_ms || [180, 95, 235]), 14050);
      scheduleEffect(() => navigator.vibrate(effect.closing_haptic_ms || [60, 40, 85]), 17050);
    }
    try {
      const AudioContextClass = globalThis.AudioContext || globalThis.webkitAudioContext;
      if (!AudioContextClass) return;
      const context = new AudioContextClass();
      audioContextsRef.current.add(context);
      const tone = (frequency, start, duration, volume = 0.045, pan = 0) => {
        const oscillator = context.createOscillator();
        const gain = context.createGain();
        const stereo = context.createStereoPanner?.();
        oscillator.type = "triangle";
        oscillator.frequency.setValueAtTime(frequency, context.currentTime + start);
        gain.gain.setValueAtTime(0.0001, context.currentTime + start);
        gain.gain.exponentialRampToValueAtTime(volume, context.currentTime + start + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + start + duration);
        oscillator.connect(gain);
        if (stereo) { stereo.pan.value = pan; gain.connect(stereo); stereo.connect(context.destination); }
        else gain.connect(context.destination);
        oscillator.start(context.currentTime + start);
        oscillator.stop(context.currentTime + start + duration + 0.02);
      };
      if (isSpark) {
        tone(1120, 0, .12, .055);
        if (!reducedMotion) {
          tone(820, 2.15, .16, .025, -.55); tone(980, 3.05, .13, .024, .55); tone(760, 4.15, .17, .022, -.2);
          tone(1280, 5.05, .2, .05); tone(1568, 7.55, .45, .035); tone(2093, 8.05, .65, .025);
        }
      } else if (isGoldenRay) {
        // A distinct low-frequency storm signature: distant tension, one impact and a long thunder tail.
        const storm = context.createBuffer(1, Math.ceil(context.sampleRate * 7.8), context.sampleRate);
        const channel = storm.getChannelData(0);
        for (let index = 0; index < channel.length; index += 1) {
          const time = index / context.sampleRate;
          const distant = Math.max(0, 1 - time / 2) * 0.12;
          const thunder = time < 3.9 ? 0 : Math.exp(-(time - 3.9) / 1.65) * 0.42;
          channel[index] = (Math.random() * 2 - 1) * (distant + thunder);
        }
        const source = context.createBufferSource();
        const filter = context.createBiquadFilter();
        const gain = context.createGain();
        source.buffer = storm; filter.type = "lowpass"; filter.frequency.value = 240;
        gain.gain.value = reducedMotion ? 0.025 : 0.085;
        source.connect(filter); filter.connect(gain); gain.connect(context.destination); source.start();
        tone(58, 0, 1.8, .025); tone(46, 3.92, 2.2, .09);
        tone(1720, 2.02, .11, .035); tone(93, 4.18, 2.8, .055);
      } else if (isObsidianFlame) {
        const burnDuration = reducedMotion ? 4.5 : 12.7;
        const fire = context.createBuffer(1, Math.ceil(context.sampleRate * burnDuration), context.sampleRate);
        const channel = fire.getChannelData(0);
        for (let index = 0; index < channel.length; index += 1) {
          const time = index / context.sampleRate;
          const rise = Math.min(1, time / 2.6);
          const fall = time > burnDuration - 2 ? Math.max(0, (burnDuration - time) / 2) : 1;
          const crackle = Math.random() > 0.992 ? (Math.random() * 2 - 1) * 0.8 : 0;
          channel[index] = ((Math.random() * 2 - 1) * 0.18 + crackle) * rise * fall;
        }
        const source = context.createBufferSource();
        const filter = context.createBiquadFilter();
        const gain = context.createGain();
        source.buffer = fire;
        filter.type = "bandpass";
        filter.frequency.value = 510;
        filter.Q.value = 0.7;
        gain.gain.value = reducedMotion ? 0.018 : 0.048;
        source.connect(filter); filter.connect(gain); gain.connect(context.destination); source.start();
        tone(72, 0, 1.9, .032);
        tone(118, 2.55, .85, .045, -.18);
        tone(185, 5.55, 1.15, .05, .18);
        tone(246.94, 9.55, .8, .033);
        tone(392, 12.05, 1.15, .022);
      } else if (isPortal9) {
        const portalDuration = reducedMotion ? 5.5 : 16.8;
        const field = context.createBuffer(1, Math.ceil(context.sampleRate * portalDuration), context.sampleRate);
        const channel = field.getChannelData(0);
        for (let index = 0; index < channel.length; index += 1) {
          const time = index / context.sampleRate;
          const rise = Math.min(1, time / 4.8);
          const sustain = time < portalDuration - 2 ? 1 : Math.max(0, (portalDuration - time) / 2);
          const shimmer = Math.sin(time * 17.5) * .035 + Math.sin(time * 31.2) * .018;
          channel[index] = ((Math.random() * 2 - 1) * .075 + shimmer) * rise * sustain;
        }
        const source = context.createBufferSource();
        const filter = context.createBiquadFilter();
        const gain = context.createGain();
        source.buffer = field;
        filter.type = "bandpass";
        filter.frequency.value = 680;
        filter.Q.value = .55;
        gain.gain.value = reducedMotion ? .014 : .036;
        source.connect(filter); filter.connect(gain); gain.connect(context.destination); source.start();
        tone(55, 0, 1.7, .027);
        tone(146.83, 2.05, 1.2, .026, -.35);
        tone(220, 4.95, 1.5, .036, .35);
        tone(329.63, 8.95, 1.8, .038, -.18);
        tone(493.88, 12.95, 1.35, .052, .18);
        tone(739.99, 13.2, 1.8, .035);
        tone(369.99, 16.0, 1.35, .022);
      } else if (isDeltaSeal) {
        const resonanceDuration = reducedMotion ? 6 : 18.6;
        const resonance = context.createBuffer(1, Math.ceil(context.sampleRate * resonanceDuration), context.sampleRate);
        const channel = resonance.getChannelData(0);
        for (let index = 0; index < channel.length; index += 1) {
          const time = index / context.sampleRate;
          const rise = Math.min(1, time / 5.4);
          const decay = time > resonanceDuration - 3 ? Math.max(0, (resonanceDuration - time) / 3) : 1;
          const harmonic = Math.sin(time * 24) * .018 + Math.sin(time * 48.5) * .011;
          channel[index] = ((Math.random() * 2 - 1) * .035 + harmonic) * rise * decay;
        }
        const source = context.createBufferSource();
        const filter = context.createBiquadFilter();
        const gain = context.createGain();
        source.buffer = resonance;
        filter.type = "bandpass";
        filter.frequency.value = 920;
        filter.Q.value = 1.15;
        gain.gain.value = reducedMotion ? .012 : .03;
        source.connect(filter); filter.connect(gain); gain.connect(context.destination); source.start();
        tone(110, 0, 1.2, .023);
        tone(261.63, 2.15, .95, .027, -.55);
        tone(329.63, 3.35, .95, .029, .55);
        tone(392, 4.65, 1.1, .032);
        tone(98, 9.55, 1.15, .072);
        tone(523.25, 9.72, .72, .052);
        tone(659.25, 10.05, .92, .04);
        tone(783.99, 13.95, 1.5, .035, -.22);
        tone(1046.5, 14.18, 1.25, .028, .22);
        tone(392, 17.1, 1.55, .02);
      } else if (isSolarCrown) {
        const solarDuration = reducedMotion ? 6 : 18.8;
        const shimmer = context.createBuffer(1, Math.ceil(context.sampleRate * solarDuration), context.sampleRate);
        const channel = shimmer.getChannelData(0);
        for (let index = 0; index < channel.length; index += 1) {
          const time = index / context.sampleRate;
          const rise = Math.min(1, time / 6);
          const decay = time > solarDuration - 3 ? Math.max(0, (solarDuration - time) / 3) : 1;
          const harmonic = Math.sin(time * 31) * .014 + Math.sin(time * 62.4) * .009 + Math.sin(time * 93.2) * .005;
          channel[index] = ((Math.random() * 2 - 1) * .025 + harmonic) * rise * decay;
        }
        const source = context.createBufferSource();
        const filter = context.createBiquadFilter();
        const gain = context.createGain();
        source.buffer = shimmer;
        filter.type = "bandpass";
        filter.frequency.value = 1280;
        filter.Q.value = .72;
        gain.gain.value = reducedMotion ? .01 : .025;
        source.connect(filter); filter.connect(gain); gain.connect(context.destination); source.start();
        tone(65.41, 0, 2.1, .022);
        tone(130.81, 2.45, 1.35, .024, -.35);
        tone(196, 3.2, 1.5, .025, .35);
        tone(261.63, 5.95, 1.7, .035, -.18);
        tone(329.63, 6.12, 1.55, .032, .18);
        tone(392, 9.95, 1.55, .036);
        tone(523.25, 13.95, 1.3, .058, -.2);
        tone(659.25, 14.05, 1.4, .05, .2);
        tone(783.99, 14.18, 1.55, .04);
        tone(1046.5, 17.0, 1.8, .024);
      } else if (isKhaosEye) {
        const visionDuration = reducedMotion ? 6 : 18.8;
        const field = context.createBuffer(1, Math.ceil(context.sampleRate * visionDuration), context.sampleRate);
        const channel = field.getChannelData(0);
        for (let index = 0; index < channel.length; index += 1) {
          const time = index / context.sampleRate;
          const rise = Math.min(1, time / 5.6);
          const decay = time > visionDuration - 3 ? Math.max(0, (visionDuration - time) / 3) : 1;
          const whisper = Math.sin(time * 13.4) * .019 + Math.sin(time * 26.8) * .012 + Math.sin(time * 53.6) * .006;
          channel[index] = ((Math.random() * 2 - 1) * .04 + whisper) * rise * decay;
        }
        const source = context.createBufferSource();
        const filter = context.createBiquadFilter();
        const gain = context.createGain();
        source.buffer = field;
        filter.type = "bandpass";
        filter.frequency.value = 540;
        filter.Q.value = 1.4;
        gain.gain.value = reducedMotion ? .012 : .032;
        source.connect(filter); filter.connect(gain); gain.connect(context.destination); source.start();
        tone(48.99, 0, 2.1, .026);
        tone(98, 2.35, 1.35, .032, -.4);
        tone(146.83, 5.95, 1.3, .038, .4);
        tone(220, 9.95, 1.55, .043);
        tone(329.63, 13.95, 1.1, .06, -.24);
        tone(440, 14.08, 1.2, .052, .24);
        tone(659.25, 14.22, 1.5, .04);
        tone(293.66, 17.0, 1.65, .026);
      } else if (isAncestralStar) {
        const ancestralDuration = reducedMotion ? 6 : 18.9;
        const field = context.createBuffer(1, Math.ceil(context.sampleRate * ancestralDuration), context.sampleRate);
        const channel = field.getChannelData(0);
        for (let index = 0; index < channel.length; index += 1) {
          const time = index / context.sampleRate;
          const rise = Math.min(1, time / 6);
          const decay = time > ancestralDuration - 3 ? Math.max(0, (ancestralDuration - time) / 3) : 1;
          const harmonic = Math.sin(time * 16.5) * .015 + Math.sin(time * 33) * .011 + Math.sin(time * 66) * .006;
          channel[index] = ((Math.random() * 2 - 1) * .022 + harmonic) * rise * decay;
        }
        const source = context.createBufferSource();
        const filter = context.createBiquadFilter();
        const gain = context.createGain();
        source.buffer = field;
        filter.type = "bandpass";
        filter.frequency.value = 760;
        filter.Q.value = .9;
        gain.gain.value = reducedMotion ? .011 : .028;
        source.connect(filter); filter.connect(gain); gain.connect(context.destination); source.start();
        tone(55, 0, 2.2, .024);
        tone(164.81, 2.35, 1.3, .028, -.4);
        tone(220, 3.3, 1.25, .026, .4);
        tone(329.63, 5.95, 1.4, .034, -.25);
        tone(493.88, 6.15, 1.45, .034, .25);
        tone(659.25, 9.95, 1.6, .038);
        tone(880, 13.95, 1.4, .06, -.18);
        tone(1174.66, 14.1, 1.55, .05, .18);
        tone(1567.98, 14.22, 1.7, .038);
        tone(523.25, 17.0, 1.8, .024);
      } else {


        const frequencies = { deep_ray: 420, low_flame: 185, spatial: 310,
          clean_hit: 520, ceremonial: 392, khaos: 145, ancestral: 261 };
        tone(frequencies[effect.tone] || 660, 0, Math.min(1.25, .4 + (item?.priority || 1) * .18), .06);
      }
      const closeAfter = isAncestralStar
        ? (reducedMotion ? 6500 : 20500)
        : (isKhaosEye
          ? (reducedMotion ? 6500 : 20500)
          : (isSolarCrown
            ? (reducedMotion ? 6500 : 20500)
            : (isDeltaSeal
              ? (reducedMotion ? 6500 : 20500)
              : (isPortal9
                ? (reducedMotion ? 6000 : 18500)
                : (isObsidianFlame
                  ? (reducedMotion ? 5000 : 14500)
                  : ((isSpark || isGoldenRay) && !reducedMotion ? 10500 : 2600))))));
      scheduleEffect(() => { audioContextsRef.current.delete(context); context.close().catch(() => {}); }, closeAfter);
    } catch (_) { /* Audio is enhancement-only and must never block live controls. */ }
  }, [reducedMotion, scheduleEffect]);

  const enqueueOffering = useCallback((item) => {
    if (!item?.id || seenOfferingIds.current.has(item.id)) return;
    seenOfferingIds.current.add(item.id);
    offeringQueue.current.push(item);
    offeringQueue.current.sort((a, b) => (b.priority || 1) - (a.priority || 1));
    setOfferingQueueCount(offeringQueue.current.length);
    if (showingOffering.current) return;
    const showNext = () => {
      const next = offeringQueue.current.shift();
      setOfferingQueueCount(offeringQueue.current.length);
      if (!next) { showingOffering.current = false; setBurst(null); return; }
      showingOffering.current = true;
      setBurst({ ...next, queue_count: offeringQueue.current.length });
      playOfferingSound(next);
      const isTenSecondEvent = next.gift_code === "spark" || next.gift_code === "golden-ray";
      const isObsidianFlame = next.gift_code === "obsidian-flame";
      const isPortal9 = next.gift_code === "portal-9";
      const isDeltaSeal = next.gift_code === "delta-seal";
      const isSolarCrown = next.gift_code === "solar-crown";
      const isKhaosEye = next.gift_code === "khaos-eye";
      const isAncestralStar = next.gift_code === "ancestral-star";
      const duration = isAncestralStar
        ? (next.effect?.duration_ms || 20000)
        : (isKhaosEye
          ? (next.effect?.duration_ms || 20000)
          : (isSolarCrown
            ? (next.effect?.duration_ms || 20000)
            : (isDeltaSeal
              ? (next.effect?.duration_ms || 20000)
              : (isPortal9
                ? (next.effect?.duration_ms || 18000)
                : (isObsidianFlame
                  ? (next.effect?.duration_ms || 14000)
                  : (reducedMotion ? (isTenSecondEvent ? 2200 : 900) : (next.effect?.duration_ms || 3000)))))));
      scheduleEffect(() => {
        setBurst(null);
        scheduleEffect(showNext, reducedMotion ? 50 : 180);
      }, duration);
    };
    showNext();
  }, [reducedMotion, playOfferingSound, scheduleEffect]);

  useEffect(() => {
    const effectTimers = effectTimersRef.current;
    const audioContexts = audioContextsRef.current;
    const localOfferingIds = localOfferingIdsRef.current;
    const offeringKeys = offeringKeysRef.current;
    const seenOfferings = seenOfferingIds.current;
    const seenWheelEvents = seenWheelEventIdsRef.current;
    return () => {
      effectTimers.forEach((timer) => window.clearTimeout(timer));
      effectTimers.clear();
      audioContexts.forEach((context) => context.close().catch(() => {}));
      audioContexts.clear();
      localOfferingIds.clear();
      offeringKeys.clear();
      sendingGiftRef.current = false;
      wheelSpinKeyRef.current = "";
      spinningRef.current = false;
      offeringQueue.current = [];
      setOfferingQueueCount(0);
      showingOffering.current = false;
      seenOfferings.clear();
      wheelEventQueueRef.current = [];
      showingWheelEventRef.current = false;
      seenWheelEvents.clear();
      setWheelExperience(null);
      setActivePanel(null);
    };
  }, [sessionId]);

  useEffect(() => {
    const source = new EventSource(`/api/live/${sessionId}/reward-events`, { withCredentials: true });
    const onReward = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (message?.type === "reward" && message.payload) enqueueOffering(message.payload);
      } catch (_) { /* Malformed transport events are ignored; the polling fallback remains active. */ }
    };
    source.addEventListener("reward", onReward);
    return () => { source.removeEventListener("reward", onReward); source.close(); };
  }, [sessionId, enqueueOffering]);

  useEffect(() => {
    const media = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(Boolean(media?.matches));
    media?.addEventListener?.("change", update);
    return () => media?.removeEventListener?.("change", update);
  }, []);

  useEffect(() => {
    const viewport = globalThis.visualViewport;
    const update = () => {
      const viewportHeight = viewport?.height || window.innerHeight;
      const keyboardInset = Math.max(0, window.innerHeight - viewportHeight - (viewport?.offsetTop || 0));
      document.documentElement.style.setProperty("--temple-vh", `${viewportHeight}px`);
      document.documentElement.style.setProperty("--temple-keyboard-inset", `${keyboardInset}px`);
    };
    update();
    viewport?.addEventListener("resize", update);
    viewport?.addEventListener("scroll", update);
    return () => {
      viewport?.removeEventListener("resize", update);
      viewport?.removeEventListener("scroll", update);
      document.documentElement.style.removeProperty("--temple-vh");
      document.documentElement.style.removeProperty("--temple-keyboard-inset");
    };
  }, []);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const [chatResponse, offeringResponse, stateResponse] = await Promise.all([
          api.get(`/api/live/${sessionId}/chat`, { params: { limit: 60 } }),
          api.get(`/api/live/${sessionId}/offerings`, { params: { limit: 30 } }),
          api.get(`/api/live/${sessionId}/interaction-state`),
        ]);
        if (!active) return;
        const chatRows = chatResponse.data || [];
        seenChatIdsRef.current = new Set(chatRows.map((item) => item.id));
        setChat(chatRows);
        const rows = offeringResponse.data || [];
        if (!lastOffering.current) rows.forEach((item) => seenOfferingIds.current.add(item.id));
        if (lastOffering.current && rows[0]?.id !== lastOffering.current) {
          enqueueOffering(rows[0]);
        }
        lastOffering.current = rows[0]?.id || lastOffering.current;
        setGoal(stateResponse.data.goal);
        const tapEvent = stateResponse.data.taps?.[0];
        if (tapEvent && lastTapEvent.current && tapEvent.id !== lastTapEvent.current) {
          const remoteRays = Array.from({ length: Math.min(6, tapEvent.count || 1) }, (_, index) => ({
            id: `remote-${tapEvent.id}-${index}`,
            x: 40 + Math.random() * Math.max(80, window.innerWidth - 80),
            y: window.innerHeight * (0.35 + Math.random() * 0.45),
          }));
          setRays((current) => [...current.slice(-12), ...remoteRays]);
          window.setTimeout(() => setRays((current) => current.filter((item) => !item.id.startsWith(`remote-${tapEvent.id}`))), 1100);
        }
        lastTapEvent.current = tapEvent?.id || lastTapEvent.current;
        const goalEvent = stateResponse.data.goal_events?.[0];
        if (goalEvent && lastGoalEvent.current && goalEvent.id !== lastGoalEvent.current) {
          setGoalBurst(goalEvent);
          window.setTimeout(() => setGoalBurst(null), 4200);
        }
        lastGoalEvent.current = goalEvent?.id || lastGoalEvent.current;
      } catch (err) {
        setError(formatApiError(err));
      }
    };
    refresh();
    const timer = window.setInterval(refresh, 1500);
    return () => { active = false; window.clearInterval(timer); };
  }, [sessionId, enqueueOffering]);

  useEffect(() => {
    const source = new EventSource(`/api/live/${sessionId}/chat-events`, { withCredentials: true });
    const onMessage = (event) => {
      try { appendChat(JSON.parse(event.data)); }
      catch (_) { /* Invalid transport data is ignored; history polling remains the fallback. */ }
    };
    source.addEventListener("comment", onMessage);
    return () => {
      source.removeEventListener("comment", onMessage);
      source.close();
    };
  }, [sessionId, appendChat]);

  useEffect(() => {
    const source = new EventSource(`/api/live/${sessionId}/games/events/stream`, { withCredentials: true });
    const onWheelResult = (event) => {
      try { enqueueWheelEvent(JSON.parse(event.data)); }
      catch (_) { /* Invalid wheel events are ignored; the session stream remains connected. */ }
    };
    source.addEventListener("wheel_result", onWheelResult);
    return () => {
      source.removeEventListener("wheel_result", onWheelResult);
      source.close();
    };
  }, [sessionId, enqueueWheelEvent]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      chatHistoryRef.current?.scrollTo({
        top: chatHistoryRef.current.scrollHeight,
        behavior: reducedMotion ? "auto" : "smooth",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [chat.length, joinEvents.length, reducedMotion]);

  useEffect(() => {
    seenChatIdsRef.current.clear();
    seenPresenceEventIdsRef.current.clear();
    presenceEventsInitializedRef.current = false;
    setChat([]);
    setJoinEvents([]);
  }, [sessionId]);

  useEffect(() => {
    let active = true;
    const refreshLiveTools = async () => {
      try {
        await api.post(`/api/live/${sessionId}/viewer-heartbeat`);
        const requests = [
          api.get(`/api/live/${sessionId}/games/wheel`),
          api.get(`/api/live/${sessionId}/presence`),
        ];
        if (connection.is_host) {
          requests.push(api.get(`/api/live/${sessionId}/host-presence`));
          requests.push(api.get(`/api/live/${sessionId}/pulse`));
        }
        const responses = await Promise.all(requests);
        if (!active) return;
        setWheel(responses[0].data);
        const publicPresence = responses[1].data || { count: 0, system_events: [] };
        processPresenceEvents(publicPresence.system_events);
        if (connection.is_host) {
          const privatePresence = responses[2].data;
          setPresence({
            ...privatePresence,
            count: publicPresence.count,
            system_events: publicPresence.system_events || [],
          });
          setPulse(responses[3].data);
        } else {
          setPresence({
            count: publicPresence.count || 0,
            peak: 0,
            viewers: [],
            system_events: publicPresence.system_events || [],
          });
        }
      } catch (err) {
        if (err?.response?.status !== 403) setError(formatApiError(err));
      }
    };
    refreshLiveTools();
    const timer = window.setInterval(refreshLiveTools, 5000);
    return () => { active = false; window.clearInterval(timer); };
  }, [sessionId, connection.is_host, processPresenceEvents]);

  useEffect(() => {
    const timer = window.setInterval(async () => {
      const count = Math.min(9, tapQueue.current);
      if (!count) return;
      tapQueue.current -= count;
      try {
        await api.post(`/api/live/${sessionId}/taps`, { count });
      } catch (err) {
        tapQueue.current = 0;
        if (err?.response?.status !== 429) setError(formatApiError(err));
      }
    }, 550);
    return () => window.clearInterval(timer);
  }, [sessionId]);

  const tap = (event) => {
    if (connection.is_host) return;
    tapQueue.current = Math.min(18, tapQueue.current + 1);
    const rect = event.currentTarget.getBoundingClientRect();
    const ray = { id: `${Date.now()}-${Math.random()}`, x: event.clientX - rect.left, y: event.clientY - rect.top };
    setRays((current) => [...current.slice(-15), ray]);
    window.setTimeout(() => setRays((current) => current.filter((item) => item.id !== ray.id)), 1100);
  };

  const sendChat = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!message.trim() || sendingCommentRef.current) return;
    sendingCommentRef.current = true;
    setSendingComment(true);
    const idempotencyKey = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    try {
      const response = await api.post(`/api/live/${sessionId}/chat`, {
        text: message.trim(), idempotency_key: idempotencyKey,
      });
      appendChat(response.data);
      setMessage("");
      chatInputRef.current?.focus({ preventScroll: true });
    } catch (_) { setError("No pudimos enviar el comentario. Intenta nuevamente."); }
    finally {
      sendingCommentRef.current = false;
      setSendingComment(false);
    }
  };

  const sendOffering = async (gift) => {
    if (sendingGiftRef.current) return;
    sendingGiftRef.current = true;
    let idempotencyKey = offeringKeysRef.current.get(gift.id);
    if (!idempotencyKey) {
      idempotencyKey = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
      offeringKeysRef.current.set(gift.id, idempotencyKey);
    }
    setSendingGiftId(gift.id); setNeedsRecharge(false);
    try {
      const response = await api.post(`/api/live/${sessionId}/offerings`, {
        gift_id: gift.id, idempotency_key: idempotencyKey,
        message: giftMessage.trim() || null,
      });
      offeringKeysRef.current.delete(gift.id);
      localOfferingIdsRef.current.add(response.data.id);
      enqueueOffering(response.data);
      await onWallet();
      setGiftMessage("");
      setActivePanel(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "INSUFFICIENT_ENERGY") {
        setError("Energía KD9 insuficiente · Recargar"); setNeedsRecharge(true);
      } else setError("No pudimos enviar la recompensa. Intenta nuevamente.");
    } finally {
      sendingGiftRef.current = false;
      setSendingGiftId(null);
    }
  };

  const configureWheel = async () => {
    try {
      const payload = {
        enabled: Boolean(wheel.enabled),
        title: wheel.title?.trim() || "Ruleta del Templo",
        spin_cost_energy: Math.max(0, Number(wheel.spin_cost_energy) || 0),
        options: wheel.options.length >= 2 ? wheel.options : [
          { label: "Pregunta del Oráculo", kind: "question", value: 0, payload: "¿Qué aprendizaje te deja este encuentro?" },
          { label: "Bono Chispa", kind: "bonus", value: 9, payload: "Recibes 9 Energía KD" },
          { label: "Regalo virtual", kind: "virtual_gift", value: 0, payload: "Sello de gratitud del Templo" },
          { label: "Mensaje del anfitrión", kind: "message", value: 0, payload: "Tu presencia transforma el espacio" },
        ],
      };
      const { data } = await api.put(`/api/live/${sessionId}/games/wheel`, payload);
      setWheel(data);
    } catch (err) { setError(formatApiError(err)); }
  };

  const spinWheel = async () => {
    if (spinningRef.current) return;
    spinningRef.current = true;
    if (!wheelSpinKeyRef.current) {
      wheelSpinKeyRef.current = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    }
    setSpinning(true); setWheelResult(null);
    try {
      const { data } = await api.post(`/api/live/${sessionId}/games/wheel/spin`, {
        idempotency_key: wheelSpinKeyRef.current,
      });
      wheelSpinKeyRef.current = "";
      if ((wheel.spin_cost_energy || 0) > 0) await onWallet();
      window.setTimeout(() => {
        setWheelResult(data);
        spinningRef.current = false;
        setSpinning(false);
      }, reducedMotion ? 100 : 1300);
    } catch (err) {
      spinningRef.current = false;
      setSpinning(false);
      setError(formatApiError(err));
    }
  };

  const activityRows = [
    ...chat.map((item) => ({ ...item, activity_type: "comment" })),
    ...joinEvents.map((item) => ({
      id: `join-${item.id}`,
      activity_type: "join",
      created_at: item.created_at,
      display_name: item.display_name,
      text: `${item.display_name} se unió`,
    })),
  ].sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || ""))).slice(-80);
  const wheelActive = Boolean(wheel.enabled && wheel.status === "active");

  return (
    <div className="fixed inset-x-0 top-0 z-[100] overflow-hidden bg-black" style={{ height: "var(--temple-vh, 100dvh)" }}>
      <LiveKitRoom
        key={`${sessionId}-${roomAttempt}`}
        serverUrl={connection.server_url}
        token={connection.participant_token}
        connect
        audio={false}
        video={false}
        onConnected={() => {
          reconnectingRef.current = false;
          setRoomStatus("connected");
          setError((current) => current.startsWith("Conexión interrumpida") ? "" : current);
        }}
        onError={(roomError) => {
          const message = liveConnectionErrorMessage(roomError);
          setRoomStatus("error");
          setError(message);
          if (connection.is_host) setMediaState({ status: "error", message });
        }}
        onDisconnected={handleDisconnected}
        data-lk-theme="default"
        className="relative h-full w-full"
      >
        <HostMediaPublisher isHost={connection.is_host} retryKey={mediaRetry} onState={setMediaState} />
        <HostVideo isHost={connection.is_host} />
        <RoomAudioRenderer />

        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-transparent to-black/80" />

        {connection.is_host && mediaState.status !== "ready" && (
          <div className="absolute inset-x-3 top-36 z-[65] rounded-2xl border border-cyan-300/35 bg-[#061019]/95 p-4 text-sm text-white shadow-[0_0_45px_rgba(34,211,238,.18)] backdrop-blur-xl" role="status">
            <div className="flex items-start gap-3">
              <Radio className="mt-0.5 shrink-0 text-cyan-200" size={20} />
              <div className="min-w-0 flex-1">
                <b className="font-cinzel text-cyan-100">
                  {mediaState.status === "error" ? "Cámara del anfitrión sin activar" : "Preparando transmisión"}
                </b>
                <p className="mt-1 text-xs text-white/70">{mediaState.message || "Conectando cámara y micrófono…"}</p>
                {mediaState.status === "error" && (
                  <button type="button" className="kd9-btn-primary mt-3 min-h-10 px-4 text-xs"
                    onClick={(event) => {
                      event.stopPropagation();
                      setMediaState({ status: "starting", message: "Reintentando cámara y micrófono…" });
                      setMediaRetry((retry) => retry + 1);
                    }}>
                    Reintentar cámara
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        <button className="absolute inset-0 z-10 cursor-default" onClick={tap} aria-label="Enviar rayito KD visual">
          {rays.map((ray) => (
            <span key={ray.id} className="pointer-events-none absolute animate-ping text-3xl text-[#e5bd64]"
              style={{ left: ray.x - 12, top: ray.y - 12 }}>⚡</span>
          ))}
        </button>

        <div className="absolute left-3 right-14 top-[max(.75rem,env(safe-area-inset-top))] z-30 rounded-2xl border border-[#c69a3a]/50 bg-black/65 p-3 backdrop-blur-md">
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="flex items-center gap-2 font-cinzel kd9-gold-text"><Target size={15} /> Meta del Templo</span>
            <b>{goal.progress_energy || 0} / {goal.target_energy || 3000} KD</b>
          </div>
          <div className="relative mt-2 h-2 overflow-visible rounded-full bg-white/15">
            <div className="h-full rounded-full bg-gradient-to-r from-[#8a6423] to-[#f4d47c] transition-all duration-700"
              style={{ width: `${Math.min(100, goal.percent || 0)}%` }} />
            {[25, 50, 75, 100].map((mark) => <span key={mark} className="absolute top-[-3px] h-3 w-px bg-white/70" style={{ left: `${mark}%` }} aria-hidden="true" />)}
          </div>
        </div>

        <button onClick={(event) => { event.stopPropagation(); requestExit(); }}
          className="absolute right-3 top-3 z-40 rounded-full border border-white/25 bg-black/65 p-2 text-white"
          aria-label="Salir del live"><X size={20} /></button>

        <div className="absolute left-3 top-24 z-30 max-w-[52%] rounded-xl bg-black/45 px-3 py-2 backdrop-blur-sm">
          <div className="flex items-center gap-2"><span className="rounded bg-red-700 px-2 py-0.5 text-[10px] font-bold">EN VIVO</span><b className="font-cinzel text-sm kd9-gold-text">{selected.first_name || "Templo Vivo KD9"}</b></div>
          {selected.title && <p className="mt-1 text-xs text-white/80">{selected.title}</p>}
        </div>

        <div className="absolute right-3 top-24 z-40 flex max-w-[46%] flex-wrap justify-end gap-2" onClick={(event) => event.stopPropagation()}>
          {connection.is_host ? (
            <button type="button" disabled={Boolean(wheelExperience)} onClick={() => togglePanel("host")} aria-expanded={hostPanelOpen}
              className="kd9-live-top-chip inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-[11px] font-bold disabled:opacity-35"
              aria-label={`${presence.count} conectados. Abrir lista privada`}>
              <Users size={15} /> {presence.count}
            </button>
          ) : (
            <div className="kd9-live-top-chip inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-[11px] font-bold" aria-label={`${presence.count} conectados`}>
              <Users size={15} /> {presence.count}
            </div>
          )}
          {(connection.is_host || wheelActive) && (
            <button type="button" disabled={Boolean(wheelExperience)} onClick={() => togglePanel("games")} aria-expanded={gamesOpen}
              className="kd9-live-top-chip inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-[11px] font-bold disabled:opacity-35">
              <Dices size={15} /> Ruleta
              <span className={`h-1.5 w-1.5 rounded-full ${wheelActive ? "bg-black" : "bg-black/40"}`} aria-hidden="true" />
            </button>
          )}
          {!connection.is_host && (
            <button type="button" disabled={Boolean(wheelExperience)} onClick={() => togglePanel("offerings")} aria-expanded={giftOpen}
              className="kd9-live-top-chip inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-[11px] font-bold disabled:opacity-35">
              <Gift size={15} /> Ofrendas
            </button>
          )}
        </div>

        {!gamesOpen && !wheelExperience && (
          <section className="kd9-live-comments absolute left-3 right-3 z-[70] sm:right-auto sm:w-[min(76vw,360px)]" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => event.stopPropagation()} aria-label="Actividad y comentarios del live">
            <div ref={chatHistoryRef} className="kd9-live-comment-history mb-2 max-h-[min(13rem,34dvh)] space-y-1 overflow-y-auto overscroll-contain pr-1 [mask-image:linear-gradient(transparent,black_18%)]" aria-live="polite">
              {activityRows.map((item) => item.activity_type === "join" ? (
                <div key={item.id} className="w-fit max-w-full rounded-xl bg-black/35 px-3 py-1 text-xs text-cyan-100/90 backdrop-blur-sm">
                  <Users size={12} className="mr-1.5 inline" />{item.text}
                </div>
              ) : (
                <div key={item.id} className="w-fit max-w-full break-words rounded-xl bg-black/55 px-3 py-1.5 text-sm text-white backdrop-blur-sm">
                  <b className="mr-2 kd9-gold-text">{item.sender_name}</b>{item.text}
                </div>
              ))}
            </div>
            <form className="kd9-live-comment-composer flex items-center gap-2 rounded-2xl border border-[#e5bd64]/45 bg-black/88 p-2 shadow-[0_0_28px_rgba(229,189,100,.14)] backdrop-blur-xl" onSubmit={sendChat}>
              <input ref={chatInputRef} maxLength="1000" enterKeyHint="send" autoComplete="off"
                className="min-h-11 min-w-0 flex-1 rounded-xl border border-white/15 bg-white/10 px-3 py-2 text-base text-white outline-none placeholder:text-white/55 focus:border-[#e5bd64]"
                placeholder="Añade un comentario…" value={message} onChange={(event) => setMessage(event.target.value)} />
              <button type="submit" disabled={sendingComment || !message.trim()} className="min-h-11 shrink-0 rounded-xl bg-[#ffd21f] px-4 text-xs font-black text-black disabled:opacity-40" aria-label="Enviar comentario">
                {sendingComment ? "Enviando…" : "Enviar"}
              </button>
            </form>
          </section>
        )}

        {hostPanelOpen && connection.is_host && (
          <aside className="absolute right-3 top-36 z-50 w-[min(90vw,22rem)] max-h-[58dvh] overflow-y-auto rounded-3xl border border-cyan-400/30 bg-[#071014]/95 p-4" onClick={(event) => event.stopPropagation()} aria-label="Panel privado del anfitrión">
            <div className="flex items-center justify-between"><h2 className="font-cinzel text-lg text-cyan-100">Pulso del Templo</h2><button onClick={() => setActivePanel(null)} aria-label="Cerrar"><X /></button></div>
            <p className="mt-1 text-xs text-white/55">Solo tú puedes ver esta información.</p>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center"><div className="rounded-xl bg-white/5 p-2"><b>{presence.count}</b><small className="block">Ahora</small></div><div className="rounded-xl bg-white/5 p-2"><b>{presence.peak}</b><small className="block">Pico</small></div><div className="rounded-xl bg-white/5 p-2"><b>{pulse?.messages || 0}</b><small className="block">Mensajes 5m</small></div></div>
            <div className="mt-3 max-h-40 space-y-1 overflow-y-auto">{presence.viewers.map((viewer) => <div key={viewer.user_id} className="rounded-lg bg-white/5 px-3 py-2 text-sm">{viewer.name}</div>)}</div>
            <div className="mt-3 border-t border-white/10 pt-2">
              {(presence.system_events || []).slice(0, 8).map((event) => <div key={event.id} className="py-1 text-xs text-cyan-100">{event.display_name} entró al templo</div>)}
            </div>
          </aside>
        )}

        {gamesOpen && (
          <section className="absolute inset-x-0 bottom-0 z-50 max-h-[82dvh] overflow-y-auto rounded-t-3xl border-t border-violet-400/40 bg-[#0b0714]/97 p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]" onClick={(event) => event.stopPropagation()} aria-label="Juegos del Templo">
            <div className="flex items-center justify-between"><div><h2 className="font-cinzel text-lg text-violet-100">Ruleta del Templo</h2><p className="text-xs text-white/55">Un giro activa un acontecimiento para todo el live.</p></div><button onClick={() => setActivePanel(null)} aria-label="Cerrar"><X /></button></div>
            <TempleWheel sessionId={sessionId} isHost={connection.is_host} wallet={wallet} onWallet={onWallet} externalPresentation
              onError={(messageText, recharge = false) => { setError(messageText); setNeedsRecharge(recharge); }} />
          </section>
        )}

        {giftOpen && (
          <div className="absolute inset-x-0 bottom-0 z-50 max-h-[76dvh] overflow-y-auto rounded-t-3xl border-t border-[#c69a3a]/50 bg-[#090806]/95 p-4 pb-7 backdrop-blur-xl"
            onClick={(event) => event.stopPropagation()}>
            <div className="mx-auto mb-3 h-1 w-12 rounded bg-white/25" />
            <div className="flex items-center justify-between">
              <div><h2 className="font-cinzel text-lg kd9-gold-text">Ofrendas KD</h2><p className="text-xs text-white/60">Saldo: {wallet.balance || 0} KD</p></div>
              <button onClick={() => setActivePanel(null)} className="rounded-full border border-white/15 p-2"><X size={18} /></button>
            </div>
            <input maxLength="240" className="kd9-input mt-3 w-full p-3" placeholder="Mensaje opcional"
              value={giftMessage} onChange={(event) => setGiftMessage(event.target.value)} />
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {catalog.filter((gift) => gift.active).map((gift) => (
                <button key={gift.id} disabled={Boolean(sendingGiftId) || wallet.frozen || wallet.balance < gift.energy_cost}
                  onClick={() => sendOffering(gift)}
                  className="min-h-20 rounded-xl border border-[#c69a3a]/25 bg-black/55 p-2 text-left disabled:opacity-35">
                  <span className="text-2xl">{gift.symbol}</span><b className="mt-1 block truncate text-[10px] kd9-gold-text">{gift.name}</b><small>{gift.energy_cost} KD</small>
                </button>
              ))}
            </div>
            <p className="mt-3 text-center text-[10px] text-white/45">Energía digital de uso interno. No es dinero, moneda ni inversión.</p>
          </div>
        )}

        <WheelExperienceOverlay experience={wheelExperience} wheel={wheel} onSpinComplete={finishWheelExperience} />

        {error && <div className="absolute left-3 right-3 top-40 z-50 rounded-xl bg-red-950/90 p-3 text-sm text-red-100">{error}{needsRecharge && <button onClick={() => window.location.assign("/app/energia-kd")} className="ml-3 rounded-lg bg-cyan-300 px-3 py-1 text-slate-950">Recargar</button>}</div>}
        {exitPromptOpen && (
          <div className="absolute inset-0 z-[90] flex items-center justify-center bg-black/75 p-5 backdrop-blur-sm" role="presentation" onClick={() => !leaving && setExitPromptOpen(false)}>
            <div role="alertdialog" aria-modal="true" aria-labelledby="temple-exit-title" aria-describedby="temple-exit-description"
              className="w-full max-w-sm rounded-3xl border border-[#c69a3a]/60 bg-[#100d08]/98 p-6 text-center shadow-[0_0_70px_rgba(198,154,58,.25)]"
              onClick={(event) => event.stopPropagation()}>
              <Shield className="mx-auto kd9-gold-text" size={34} aria-hidden="true" />
              <h2 id="temple-exit-title" className="mt-3 font-cinzel text-xl kd9-gold-text">¿Salir del Templo Vivo?</h2>
              <p id="temple-exit-description" className="mt-2 text-sm text-white/70">
                {connection.is_host
                  ? "Salir de esta pantalla no finalizará la transmisión. El live seguirá activo hasta que uses Finalizar transmisión."
                  : "Tu lugar en este live se cerrará solo cuando confirmes la salida."}
              </p>
              <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <button autoFocus disabled={leaving} onClick={() => setExitPromptOpen(false)} className="kd9-btn-primary min-h-12 px-4">Permanecer</button>
                <button disabled={leaving} onClick={confirmExit} className="kd9-btn-outline min-h-12 px-4 disabled:opacity-50">
                  {leaving ? "Saliendo…" : "Salir del templo"}
                </button>
              </div>
            </div>
          </div>
        )}
        <OfferingBurst offering={burst ? { ...burst, queue_count: Math.max(burst.queue_count || 0, offeringQueueCount) } : null} goal={goalBurst} reducedMotion={reducedMotion} />
      </LiveKitRoom>

      {roomStatus !== "connected" && (
        <div className="absolute inset-0 z-[140] flex items-center justify-center bg-black/96 p-5 text-white" role="status" data-testid="temple-room-status">
          <div className="w-full max-w-sm rounded-3xl border border-cyan-300/35 bg-[#061019] p-6 text-center shadow-[0_0_70px_rgba(34,211,238,.18)]">
            <Radio className="mx-auto text-cyan-200" size={40} />
            <h2 className="mt-4 font-cinzel text-xl text-cyan-100">
              {roomStatus === "error" ? "No se pudo abrir la transmisión" : roomStatus === "reconnecting" ? "Reconectando Templo Vivo" : "Conectando Templo Vivo"}
            </h2>
            <p className="mt-2 text-sm text-white/70">
              {error || "Estamos estableciendo la conexión segura de video. Esta pantalla ya no quedará en negro sin información."}
            </p>
            <div className="mt-5 grid gap-3">
              {(roomStatus === "error" || roomStatus === "reconnecting") && (
                <button type="button" className="kd9-btn-primary min-h-11 px-4"
                  onClick={() => {
                    reconnectingRef.current = false;
                    setRoomStatus("connecting");
                    setError("");
                    setRoomAttempt((attempt) => attempt + 1);
                  }}>
                  Reintentar conexión
                </button>
              )}
              <button type="button" className="kd9-btn-outline min-h-11 px-4" onClick={onClose}>
                Volver al panel del Templo
              </button>
            </div>
          </div>
        </div>
      )}

      {roomStatus === "connected" && connection.is_host && mediaState.status !== "ready" && (
        <div className="absolute inset-x-3 top-36 z-[145] rounded-2xl border border-cyan-300/35 bg-[#061019]/98 p-4 text-sm text-white shadow-[0_0_45px_rgba(34,211,238,.18)] backdrop-blur-xl" role="status" data-testid="temple-media-status">
          <div className="flex items-start gap-3">
            <Radio className="mt-0.5 shrink-0 text-cyan-200" size={20} />
            <div className="min-w-0 flex-1">
              <b className="font-cinzel text-cyan-100">
                {mediaState.status === "error" ? "Cámara del anfitrión sin activar" : "Preparando cámara y micrófono"}
              </b>
              <p className="mt-1 text-xs text-white/70">{mediaState.message || "Solicitando acceso a cámara y micrófono…"}</p>
              {mediaState.status === "error" && (
                <button type="button" className="kd9-btn-primary mt-3 min-h-10 px-4 text-xs"
                  onClick={() => {
                    setMediaState({ status: "starting", message: "Reintentando cámara y micrófono…" });
                    setMediaRetry((retry) => retry + 1);
                  }}>
                  Reintentar cámara
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemploVivoPage() {
  const { role } = useRole();
  const director = role === "superadmin" || role === "director";
  const [data, setData] = useState({ consultants: [], can_host: false, own_presence: null });
  const [form, setForm] = useState({ title: "", topic: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selected, setSelected] = useState(null);
  const [connection, setConnection] = useState(null);
  const [wheelSetupOpen, setWheelSetupOpen] = useState(false);
  const [auditRows, setAuditRows] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0, frozen: false });
  const refreshWallet = useCallback(async () => {
    const { data: balanceData } = await api.get("/api/energy/me/balance", {
      headers: { "Cache-Control": "no-cache" },
    });
    const nextWallet = {
      balance: Number(balanceData?.balance || 0),
      frozen: Boolean(balanceData?.frozen),
    };
    setWallet(nextWallet);
    return nextWallet;
  }, []);
  const [offeringHistory, setOfferingHistory] = useState({ sent: [], received: [], rewards: { pending_energy: 0 } });
  const [commission, setCommission] = useState(20);
  const [goalSettings, setGoalSettings] = useState({ target_energy: 3000, reward_energy: 300, enabled: true });

  const load = useCallback(async () => {
    try {
      const requests = [
        api.get(director ? "/api/director/live/overview" : "/api/live/temple"),
        api.get("/api/offerings/catalog"),
        api.get(director ? "/api/director/offerings/overview" : "/api/offerings/me"),
      ];
      if (director) {
        requests.push(api.get("/api/director/live/audit", { params: { limit: 150 } }));
        requests.push(api.get("/api/director/live/goal"));
      }
      const responses = await Promise.all(requests);
      setData(responses[0].data);
      setCatalog(responses[1].data.gifts || []);
      await refreshWallet();
      setOfferingHistory(responses[2].data);
      if (director) {
        setCommission(responses[2].data.commission_percent ?? 20);
        setAuditRows(responses[3].data.events || []);
        setGoalSettings(responses[4].data);
      }
      setError("");
    } catch (err) { setError(formatApiError(err)); }
  }, [director, refreshWallet]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 15000);
    return () => window.clearInterval(timer);
  }, [load]);

  const act = async (fn, messageText) => {
    try {
      const response = await fn();
      setNotice(messageText); setError(""); await load(); return response;
    } catch (err) { setError(formatApiError(err)); return null; }
  };

  const reasonAction = (url, messageText) => {
    const reason = window.prompt("Motivo obligatorio para auditoría");
    if (reason?.trim()) act(() => api.post(url, { reason: reason.trim() }), messageText);
  };

  const start = async (event) => {
    event.preventDefault();
    const response = await act(() => api.post("/api/live/start", form), "Transmisión iniciada.");
    if (response?.data) { setConnection(response.data); setSelected(response.data); }
  };

  const reconnectLive = useCallback(async (sessionId) => {
    const { data: liveConnection } = await api.post(`/api/live/${sessionId}/token`);
    setConnection(liveConnection);
    return liveConnection;
  }, []);

  const join = async (consultant) => {
    try {
      const liveConnection = await reconnectLive(consultant.session_id);
      setSelected(consultant);
    } catch (err) { setError(formatApiError(err)); }
  };

  const resumeOwnLive = async () => {
    const own = data.own_presence;
    if (!own?.session_id || own.status !== "live") return;
    try {
      const liveConnection = await reconnectLive(own.session_id);
      setSelected(own);
      setConnection(liveConnection);
      setNotice("Transmisión reanudada. Cámara y audio vuelven a publicarse en la misma sesión.");
      setError("");
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const updateGift = (gift, changes) => act(
    () => api.put(`/api/director/offerings/catalog/${gift.id}`, { ...gift, ...changes }),
    "Catálogo actualizado."
  );

  return (
    <PageShell
      kicker="Transmisión de anfitrión único"
      title="TEMPLO VIVO KD9"
      desc="Todos pueden entrar como espectadores; solo los Consultores con la capacidad Templo Vivo activada por Dirección pueden transmitir."
      badge={<button onClick={load} className="kd9-btn-outline rounded-lg p-2" aria-label="Actualizar"><RefreshCw size={16} /></button>}
    >
      {connection && selected && (
        <TempleRuntimeBoundary onRecover={() => { setConnection(null); setSelected(null); load(); }}>
          <LiveExperience connection={connection} selected={selected} catalog={catalog} wallet={wallet}
            onWallet={refreshWallet}
            onReconnect={reconnectLive}
            onClose={() => { setConnection(null); setSelected(null); load(); }} />
        </TempleRuntimeBoundary>
      )}
      {error && <div className="kd9-card mb-4 p-4 text-sm text-red-300">{error}</div>}
      {notice && <div className="kd9-card mb-4 p-4 text-sm kd9-gold-text">{notice}</div>}

      <div className="mb-6 grid gap-3 md:grid-cols-3">
        <div className="kd9-card p-4"><small className="uppercase text-white/55">Tu Energía KD</small><div className="mt-1 text-2xl kd9-gold-text"><Zap className="mr-2 inline" size={20} />{wallet.balance || 0} KD</div></div>
        <div className="kd9-card p-4"><small className="uppercase text-white/55">Recompensa pendiente</small><div className="mt-1 text-2xl kd9-gold-text">{offeringHistory.rewards?.pending_energy || 0} KD</div></div>
        <div className="kd9-card p-4"><small className="uppercase text-white/55">Formato</small><p className="mt-1 text-sm">Live vertical de anfitrión único con capas flotantes.</p></div>
      </div>

      {!director && !data.can_host && (
        <section className="kd9-card mb-6 border border-cyan-400/20 p-5">
          <h2 className="flex items-center gap-2 font-cinzel text-lg text-cyan-100"><Radio size={17}/> Modo espectador</h2>
          <p className="mt-2 text-sm text-white/65">Puedes entrar a todos los lives disponibles. Los controles para transmitir aparecen únicamente cuando Dirección activa la capacidad Templo Vivo para tu cuenta.</p>
        </section>
      )}

      {!director && data.can_host && (
        <section className="kd9-card mb-6 p-5">
          <div className="mb-4 flex flex-wrap gap-3">
            {["available", "busy", "offline"].map((state) => (
              <button key={state} onClick={() => act(() => api.put("/api/live/presence", { status: state }), "Estado actualizado.")} className="kd9-btn-outline px-4 py-2">{stateLabel[state]}</button>
            ))}
            {data.own_presence?.status === "live" && (
              <>
                {!connection && (
                  <button onClick={resumeOwnLive} className="kd9-btn-primary px-4 py-2">
                    <Radio size={15} /> Reanudar transmisión
                  </button>
                )}
                <button onClick={() => act(() => api.post("/api/live/stop"), "Transmisión finalizada.")} className="kd9-btn-outline px-4 py-2"><CircleStop size={15} /> Finalizar</button>
              </>
            )}
          </div>
          <div className="mt-4">
            <button type="button" className="kd9-btn-outline min-h-11 px-4 text-xs"
              onClick={() => setWheelSetupOpen((open) => !open)} aria-expanded={wheelSetupOpen}>
              <Dices size={15} /> {wheelSetupOpen ? "Ocultar configuración de ruleta" : "Configurar ruleta del Templo"}
            </button>
            {wheelSetupOpen && (
              <TempleRuntimeBoundary onRecover={() => setWheelSetupOpen(false)}>
                <TempleWheelSetup />
              </TempleRuntimeBoundary>
            )}
          </div>
          <form onSubmit={start} className="mt-4 grid gap-3 md:grid-cols-2">
            <input required minLength="3" maxLength="160" className="kd9-input p-3" placeholder="Título del en vivo" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
            <input maxLength="500" className="kd9-input p-3" placeholder="Tema o descripción" value={form.topic} onChange={(event) => setForm({ ...form, topic: event.target.value })} />
            <button className="kd9-btn-primary px-5 py-3 md:col-span-2"><Play size={16} /> Iniciar transmisión</button>
          </form>
        </section>
      )}

      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {(data.consultants || []).map((consultant) => (
          <article key={consultant.consultant_id} className="kd9-card overflow-hidden">
            <div className="relative aspect-[9/12] bg-black">
              {consultant.avatar_url ? <img src={consultant.avatar_url} alt={consultant.first_name} className="h-full w-full object-cover opacity-75" /> : <div className="flex h-full items-center justify-center"><Radio size={50} className="kd9-gold-text" /></div>}
              <span className="absolute left-3 top-3 rounded-full bg-red-700 px-3 py-1 text-[10px] font-bold text-white">{stateLabel[consultant.status]}</span>
            </div>
            <div className="p-5">
              <h3 className="font-cinzel text-xl kd9-gold-text">{consultant.first_name}</h3>
              <p className="mt-1 text-sm">{consultant.specialty}</p>
              {consultant.title && <p className="mt-3 font-cormorant text-xl">{consultant.title}</p>}
              {consultant.status === "live" && consultant.join_enabled && <button onClick={() => join(consultant)} className="kd9-btn-primary mt-4 px-4 py-2"><Play size={15} /> Entrar al live</button>}
              {director && <div className="mt-4 flex flex-wrap gap-2">
                {consultant.status === "live" && <button onClick={() => reasonAction(`/api/director/live/${consultant.consultant_id}/stop`, "Live apagado.")} className="kd9-btn-outline p-2"><CircleStop size={14} /> Apagar</button>}
                {consultant.status === "blocked"
                  ? <button onClick={() => reasonAction(`/api/director/live/${consultant.consultant_id}/unblock`, "Consultor desbloqueado.")} className="kd9-btn-outline p-2"><Unlock size={14} /> Desbloquear</button>
                  : <button onClick={() => reasonAction(`/api/director/live/${consultant.consultant_id}/block`, "Consultor bloqueado.")} className="kd9-btn-outline p-2"><Ban size={14} /> Bloquear</button>}
              </div>}
            </div>
          </article>
        ))}
      </div>

      {director && (
        <section className="kd9-card mt-6 p-5">
          <h2 className="flex items-center gap-2 font-cinzel text-xl kd9-gold-text"><Target size={18} /> Gobierno del Templo</h2>
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <label className="text-sm">Meta por live (KD)<input type="number" min="1" className="kd9-input mt-1 block w-full p-3" value={goalSettings.target_energy} onChange={(event) => setGoalSettings({ ...goalSettings, target_energy: Number(event.target.value) })} /></label>
            <label className="text-sm">Recompensa de meta (KD)<input type="number" min="0" className="kd9-input mt-1 block w-full p-3" value={goalSettings.reward_energy} onChange={(event) => setGoalSettings({ ...goalSettings, reward_energy: Number(event.target.value) })} /></label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={goalSettings.enabled} onChange={(event) => setGoalSettings({ ...goalSettings, enabled: event.target.checked })} /> Meta activa</label>
            <button className="kd9-btn-primary self-end px-4 py-3" onClick={() => act(() => api.put("/api/director/live/goal", goalSettings), "Meta configurada y auditada.")}>Guardar meta</button>
          </div>
          <div className="mt-6 flex flex-wrap items-end gap-3">
            <label className="text-sm">Comisión automática KD9 (%)<input type="number" min="0" max="100" className="kd9-input mt-1 block p-3" value={commission} onChange={(event) => setCommission(Number(event.target.value))} /></label>
            <button className="kd9-btn-primary px-4 py-3" onClick={() => act(() => api.put("/api/director/offerings/commission", { commission_percent: commission }), "Comisión actualizada.")}>Guardar comisión</button>
            <div className="ml-auto"><small>Comisión acumulada</small><div className="text-xl kd9-gold-text">{offeringHistory.commission_total || 0} KD</div></div>
          </div>
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {catalog.map((gift) => <article key={gift.id} className="rounded-xl border border-white/10 p-3"><span className="text-2xl">{gift.symbol}</span><b className="block kd9-gold-text">{gift.name}</b><small>{gift.energy_cost} KD · {gift.active ? "Activa" : "Oculta"}</small><div className="mt-2 flex gap-2"><button className="kd9-btn-outline px-2 py-1 text-[9px]" onClick={() => { const value = window.prompt("Nuevo costo", gift.energy_cost); if (value && Number(value) > 0) updateGift(gift, { energy_cost: Number(value) }); }}>Costo</button><button className="kd9-btn-outline px-2 py-1 text-[9px]" onClick={() => updateGift(gift, { active: !gift.active })}>{gift.active ? "Ocultar" : "Activar"}</button></div></article>)}
          </div>
        </section>
      )}

      {(offeringHistory.sent?.length || offeringHistory.received?.length) > 0 && <section className="kd9-card mt-6 p-5"><h2 className="flex items-center gap-2 font-cinzel kd9-gold-text"><History size={18} /> Historial de Ofrendas</h2><p className="mt-2 text-sm">Enviadas: {offeringHistory.sent?.length || 0} · Recibidas: {offeringHistory.received?.length || 0}</p></section>}

      {director && <section className="kd9-card mt-6 p-5"><h2 className="flex items-center gap-2 font-cinzel text-xl kd9-gold-text"><Sparkles size={18} /> Auditoría del Templo Vivo</h2><div className="mt-4 max-h-80 overflow-y-auto">{auditRows.map((event) => <div key={event.id} className="border-t border-white/10 py-3 text-sm"><b>{event.action}</b><div className="text-xs text-white/50">{event.at ? new Date(event.at).toLocaleString() : "—"} · {event.target || "—"}</div></div>)}</div></section>}

      {!(data.consultants || []).length && <div className="kd9-card mt-5 p-12 text-center"><Shield className="mx-auto kd9-gold-text" size={34} /><p className="mt-3">No hay consultores visibles en este momento.</p></div>}
      <RewardsKD />
    </PageShell>
  );
}

export default function TemploVivo() {
  return (
    <TempleRuntimeBoundary onRecover={() => window.location.reload()}>
      <TemploVivoPage />
    </TempleRuntimeBoundary>
  );
}